import React, { useState, useEffect, useRef } from 'react';
import api from '@/config/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, DataTable, Spinner, Badge, Button, Drawer, Divider, KPICard, Modal, FormField, PasswordField, FormDatePicker, FormReactSelect } from '../ui';
import { Users as UsersIcon, Wallet, DollarSign, TrendingUp, TrendingDown, Calendar, Mail, User, ArrowRight, Plus, Edit, Trash2, UserCircle, Copy, RefreshCw, BarChart3, Zap, Filter, Gauge, Activity, Upload } from 'lucide-react';
import { useToastContext } from '../ui/feedback/ToastProvider';
import { useConfirmContext } from '../ui/feedback/ConfirmProvider';
import DirhamSymbol from '../components/DirhamSymbol';
import BulkInvestorImportModal from '../components/BulkInvestorImportModal';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const Investors = () => {
  const { user: currentUser } = useAuth();
  const { error: showError, success: showSuccess } = useToastContext();
  const { confirm } = useConfirmContext();
  const [investors, setInvestors] = useState([]);
  const [brokerInvestors, setBrokerInvestors] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [dailyROIData, setDailyROIData] = useState({}); // Map of investorId -> dailyROI
  const [minuteROIData, setMinuteROIData] = useState({}); // Map of investorId -> minuteROI (real-time)
  const [brokerCommissionData, setBrokerCommissionData] = useState({}); // Map of investorId -> broker commission
  const [payoutData, setPayoutData] = useState([]); // Array of investor payouts
  const [loading, setLoading] = useState(true);
  const [roiLoading, setROILoading] = useState(false);
  const [checkedInvestors, setCheckedInvestors] = useState(new Set());
  const [minuteROILoading, setMinuteROILoading] = useState(false);
  const [autoRefreshMinuteROI, setAutoRefreshMinuteROI] = useState(true);
  const minuteROIIntervalRef = useRef(null);
  const [selectedLiveInvestor, setSelectedLiveInvestor] = useState('ALL');
  const [liveChartData, setLiveChartData] = useState([]);
  const [liveChartLoading, setLiveChartLoading] = useState(false);
  const [liveChartHours, setLiveChartHours] = useState('3');
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBrokerModal, setShowBrokerModal] = useState(false);
  const [createInvestorLoading, setCreateInvestorLoading] = useState(false);
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [investorDetails, setInvestorDetails] = useState({
    portfolio: null,
    transactions: [],
    payouts: [],
    roiHistory: [],
    allocatedProfits: {
      direct_allocations: [],
      broker_commissions: [],
      summary: {
        total_direct_allocated: 0,
        total_commissions_aed: 0,
        total_commissions_usdt: 0
      }
    },
    loading: false
  });
  const [allocatedProfitsPeriod, setAllocatedProfitsPeriod] = useState('30');
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'name'
  });
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    invested_amount: 0,
    roi_min: 0.5,
    roi_max: 1.5,
    startDate: null,
    broker_id: '',
    broker_min_roi: 0.5,
    broker_max_roi: 1.5,
    balance_usdt: 0
  });
  const [brokerFormData, setBrokerFormData] = useState({
    name: '',
    email: '',
    password: '',
    startDate: null
  });

  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyInvestor, setHistoryInvestor] = useState(null);
  const [editingHistoryId, setEditingHistoryId] = useState(null);
  const [editingHistoryValues, setEditingHistoryValues] = useState({
    roi_percentage: '',
    daily_profit_aed: '',
    broker_commission_aed: '',
  });
  const [savingHistory, setSavingHistory] = useState(false);
  const [regeneratingProfits, setRegeneratingProfits] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({
    startDate: null,
    endDate: null
  });

  // Investment modal state
  const [showInvestmentModal, setShowInvestmentModal] = useState(false);
  const [showInvestmentHistoryModal, setShowInvestmentHistoryModal] = useState(false);
  const [showAdjustInvestmentModal, setShowAdjustInvestmentModal] = useState(false);
  const [showInvestmentSnapshotModal, setShowInvestmentSnapshotModal] = useState(false);
  const [investmentData, setInvestmentData] = useState([]);
  const [investmentLoading, setInvestmentLoading] = useState(false);
  const [adjustInvestmentLoading, setAdjustInvestmentLoading] = useState(false);
  const [selectedInvestmentSnapshot, setSelectedInvestmentSnapshot] = useState(null);
  const [selectedInvestorForInvestment, setSelectedInvestorForInvestment] = useState(null);
  const [newInvestmentForm, setNewInvestmentForm] = useState({
    amount: '',
    date: new Date(),
    note: ''
  });
  const [adjustInvestmentForm, setAdjustInvestmentForm] = useState({
    mode: 'reduce',
    amount: '',
    date: new Date(),
    note: ''
  });

  // Chart state
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartFilters, setChartFilters] = useState({
    timeframe: '30', // 7, 14, 30, 60, 90 days
    selectedInvestors: [], // empty means all investors
    customDateRange: false, // Toggle for custom date range
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    fetchInvestors();
    fetchBrokerInvestors();
    fetchBrokers();
  }, []);

  useEffect(() => {
    if (investors.length > 0) {
      fetchChartData();
      fetchMinuteROI();
    }
  }, [chartFilters.timeframe, chartFilters.selectedInvestors, chartFilters.customDateRange, chartFilters.startDate, chartFilters.endDate, investors]);

  useEffect(() => {
    // Auto-select "ALL" or the first active investor if none selected
    if (!selectedLiveInvestor) {
      const firstActive = investors.find(inv => inv.active !== false);
      setSelectedLiveInvestor(firstActive?._id || 'ALL');
    }
  }, [investors, selectedLiveInvestor]);

  // Auto-refresh minute ROI data every 30 seconds
  useEffect(() => {
    if (!autoRefreshMinuteROI || investors.length === 0) return;

    const runFetches = () => {
      fetchMinuteROI();
      if (selectedLiveInvestor) {
        fetchMinuteROIChart(selectedLiveInvestor, liveChartHours);
      }
    };

    runFetches();
    minuteROIIntervalRef.current = setInterval(() => {
      runFetches();
    }, 30000); // Refresh every 30 seconds

    return () => {
      if (minuteROIIntervalRef.current) {
        clearInterval(minuteROIIntervalRef.current);
      }
    };
  }, [autoRefreshMinuteROI, investors.length, selectedLiveInvestor, liveChartHours]);

  const fetchInvestors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/users');
      // Filter to show only investors
      const investorsList = response.data.users.filter(user => user.role === 'investor');
      setInvestors(investorsList);

      // Fetch daily ROI, broker commissions, and payouts for each investor
      fetchDailyROI(investorsList);
      fetchBrokerCommissions(investorsList);
      fetchInvestorPayouts();
    } catch (error) {
      console.error('Error fetching investors:', error);
      showError('Failed to fetch investors');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestorPayouts = async () => {
    try {
      const response = await api.get('/api/users/payout-totals');
      setPayoutData(response.data.payoutTotals || []);
      console.log('[InvestorPayouts] Payout data received:', response.data.payoutTotals);
    } catch (error) {
      console.error('[InvestorPayouts] Error fetching payout totals:', error);
      setPayoutData([]);
    }
  };

  const fetchDailyROI = async (investorsList) => {
    try {
      setROILoading(true);
      const roiMap = {};

      // Fetch today's accumulated profit for all investors at once
      let todayProfitMap = {};
      try {
        const todayAvgResponse = await api.get('/api/minute-roi/daily-average-all');
        if (todayAvgResponse.data && todayAvgResponse.data.investors) {
          todayAvgResponse.data.investors.forEach(inv => {
            todayProfitMap[inv.investor_id] = inv.totalProfit || 0;
          });
        }
      } catch (err) {
        console.error('Error fetching today\'s average ROI:', err);
      }

      for (const investor of investorsList) {
        try {
          // Determine startDate: Use investor's startDate if available, otherwise default to a reasonable past date
          let fetchStart = investor.startDate || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000); // 1 year fallback

          // Add timestamp to prevent caching
          const response = await api.get(`/api/roi/investor/${investor._id}`, {
            params: {
              startDate: fetchStart,
              limit: 10000, // Get all records
              _t: Date.now() // Cache-busting parameter
            }
          });

          // Store records and today's accumulated profit
          roiMap[investor._id] = {
            latest: response.data.data.records && response.data.data.records.length > 0 ? response.data.data.records[0] : null,
            allRecords: response.data.data.records || [],
            todayProfitAED: todayProfitMap[investor._id] || 0
          };
        } catch (err) {
          console.log(`No ROI data for investor ${investor._id}`);
          // Still set empty structure to store today's profit if available
          roiMap[investor._id] = {
            latest: null,
            allRecords: [],
            todayProfitAED: todayProfitMap[investor._id] || 0
          };
        }
      }

      setDailyROIData(roiMap);
    } catch (error) {
      console.error('Error fetching daily ROI:', error);
    } finally {
      setROILoading(false);
    }
  };

  const fetchBrokerCommissions = async (investorsList) => {
    try {
      const commissionMap = {};
      const conversionRate = parseFloat(import.meta.env.VITE_USD_TO_AED_RATE) || 3.662;

      for (const investor of investorsList) {
        try {
          // Find which broker this investor belongs to
          const brokerInvestorResponse = await api.get(`/api/broker-investors`, {
            params: { investor_id: investor._id }
          });

          const brokerInvestorsList = brokerInvestorResponse.data?.brokerInvestors || [];
          const brokerInvestorData = brokerInvestorsList[0];
          if (!brokerInvestorData || !brokerInvestorData.broker_id) {
            continue;
          }

          const roiMin = Number(brokerInvestorData.broker_min_roi ?? brokerInvestorData.roi_min ?? 0);
          const roiMax = Number(brokerInvestorData.broker_max_roi ?? brokerInvestorData.roi_max ?? 0);
          const avgROI = (roiMin + roiMax) / 2;

          const investedAED = Number(
            investor.invested_amount_aed || ((investor.invested_amount || 0) * conversionRate)
          );

          const brokerStartDateRaw = brokerInvestorData?.broker_id?.startDate || brokerInvestorData?.createdAt;
          let brokerDays = 0;
          if (brokerStartDateRaw) {
            const brokerStartDate = new Date(brokerStartDateRaw);
            const today = new Date();
            const brokerStartDay = new Date(brokerStartDate).setHours(0, 0, 0, 0);
            const todayDay = new Date(today).setHours(0, 0, 0, 0);
            const diffMs = todayDay - brokerStartDay;
            brokerDays = diffMs >= 0 ? Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1 : 0;
          }

          // Formula requested: invested AED * avg ROI / 100 / 30 * no. of broker days
          const dailyCommissionAED = (investedAED * avgROI / 100) / 30;
          const grossCommissionAED = dailyCommissionAED * brokerDays;
          const grossCommissionUSDT = grossCommissionAED / conversionRate;

          // Fetch broker payouts for this investor
          let totalPayoutsUSDT = 0;
          try {
            const payoutsResponse = await api.get(`/api/payouts`, {
              params: {
                userId: investor._id,
                payoutType: 'broker_commission'
              }
            });
            const payouts = payoutsResponse.data?.payouts || [];
            totalPayoutsUSDT = payouts.reduce((sum, payout) => sum + (payout.amount || 0), 0);
          } catch (payoutErr) {
            totalPayoutsUSDT = 0;
          }

          const netCommissionUSDT = grossCommissionUSDT - totalPayoutsUSDT;
          const netCommissionAED = grossCommissionAED - (totalPayoutsUSDT * conversionRate);

          commissionMap[investor._id] = {
            total_aed: netCommissionAED,
            total_usdt: netCommissionUSDT,
            gross_commission_usdt: grossCommissionUSDT,
            gross_commission_aed: grossCommissionAED,
            payouts_usdt: totalPayoutsUSDT,
            monthly_commission_aed: (investedAED * avgROI / 100),
            monthly_commission_usdt: (investedAED * avgROI / 100) / conversionRate,
            today_commission_aed: dailyCommissionAED,
            today_commission_usdt: dailyCommissionAED / conversionRate,
            avg_roi: avgROI,
            broker_days: brokerDays
          };
        } catch (err) {
          console.error(`[Commission] Error calculating commission for investor ${investor._id}:`, err.response?.data?.message || err.message);
        }
      }

      setBrokerCommissionData(commissionMap);
    } catch (error) {
      console.error('[Commission] Fatal error fetching broker commissions:', error);
    }
  };

  const fetchChartData = async () => {
    try {
      setChartLoading(true);

      // Determine date range based on filter type
      let startDate, endDate, days;

      if (chartFilters.customDateRange && chartFilters.startDate && chartFilters.endDate) {
        // Use custom date range
        startDate = new Date(chartFilters.startDate);
        endDate = new Date(chartFilters.endDate);
        days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      } else {
        // Use timeframe dropdown
        days = parseInt(chartFilters.timeframe);
        endDate = new Date();
        startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
      }

      // Determine which investors to fetch data for
      const targetInvestors = chartFilters.selectedInvestors.length > 0
        ? chartFilters.selectedInvestors
        : investors.map(inv => inv._id);

      if (targetInvestors.length === 0) {
        setChartData([]);
        return;
      }

      // Fetch ROI data for each selected investor
      const dataPromises = targetInvestors.map(investorId =>
        api.get(`/api/roi/investor/${investorId}`, {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            limit: days
          }
        }).catch(err => {
          console.error(`Error fetching ROI for investor ${investorId}:`, err);
          return { data: { data: { records: [] } } };
        })
      );

      const responses = await Promise.all(dataPromises);

      // Aggregate data by date
      const dateMap = new Map();

      responses.forEach(response => {
        const records = response.data?.data?.records || [];
        records.forEach(record => {
          const dateStr = new Date(record.date).toISOString().split('T')[0];
          if (!dateMap.has(dateStr)) {
            dateMap.set(dateStr, {
              date: dateStr,
              totalDailyProfit: 0,
              count: 0
            });
          }
          const entry = dateMap.get(dateStr);
          entry.totalDailyProfit += record.daily_return || 0;
          entry.count += 1;
        });
      });

      // Convert to array and sort by date
      const chartArray = Array.from(dateMap.values())
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .map(item => ({
          date: item.date,
          displayDate: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          totalDailyProfit: parseFloat(item.totalDailyProfit.toFixed(2)),
        }));

      setChartData(chartArray);
    } catch (error) {
      console.error('Error fetching chart data:', error);
      showError('Failed to load chart data');
    } finally {
      setChartLoading(false);
    }
  };

  // Fetch live minute ROI data for all investors
  const fetchMinuteROI = async () => {
    try {
      setMinuteROILoading(true);
      const targetInvestors = investors.filter(inv => inv.active !== false).map(inv => inv._id);

      if (targetInvestors.length === 0) return;

      // Fetch current minute ROI for each investor
      const minutePromises = targetInvestors.map(investorId =>
        api.get(`/api/minute-roi/current/${investorId}`)
          .then(response => ({
            investorId,
            data: response.data.data || null
          }))
          .catch(err => {
            console.error(`Error fetching minute ROI for ${investorId}:`, err);
            return {
              investorId,
              data: null
            };
          })
      );

      const responses = await Promise.all(minutePromises);
      const roiMap = {};

      responses.forEach(({ investorId, data }) => {
        if (data) {
          roiMap[investorId] = {
            roi_percentage: data.roi_percentage || 0,
            profit_per_minute: data.profit_per_minute || 0,
            invested_amount: data.invested_amount || 0,
            current_balance: data.current_balance || 0,
            timestamp: data.timestamp || new Date().toISOString()
          };
        }
      });

      setMinuteROIData(roiMap);
    } catch (error) {
      console.error('Error fetching minute ROI data:', error);
    } finally {
      setMinuteROILoading(false);
    }
  };

  // Fetch minute-level timeseries for a specific investor
  const fetchMinuteROIChart = async (investorId, hours = liveChartHours) => {
    if (!investorId) return;
    try {
      setLiveChartLoading(true);
      const hoursParam = hours || liveChartHours || '3';

      // Helper to format records
      const formatRecords = (records) => {
        return records.map(item => {
          const ts = item.time || item.timestamp || item.createdAt || new Date();
          const d = new Date(ts);
          return {
            time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            roi: item.roi ?? item.roi_percentage ?? item.roiPercent ?? 0,
            profit: item.profit ?? item.profit_per_minute ?? 0
          };
        });
      };

      if (investorId === 'ALL') {
        const activeInvestors = investors.filter(inv => inv.active !== false).map(inv => inv._id);
        if (activeInvestors.length === 0) {
          setLiveChartData([]);
          return;
        }

        const responses = await Promise.all(activeInvestors.map(id =>
          api.get(`/api/minute-roi/chart-data/${id}`, { params: { hours: hoursParam } })
            .then(res => res.data?.data || [])
            .catch(err => {
              console.error(`Error fetching live ROI timeseries for ${id}:`, err);
              return [];
            })
        ));

        const buckets = new Map();
        responses.forEach(records => {
          formatRecords(records).forEach(item => {
            const key = item.time;
            if (!buckets.has(key)) {
              buckets.set(key, { time: key, roiSum: 0, roiCount: 0, profitSum: 0 });
            }
            const bucket = buckets.get(key);
            bucket.roiSum += item.roi;
            bucket.roiCount += 1;
            bucket.profitSum += item.profit;
          });
        });

        const aggregated = Array.from(buckets.values())
          .sort((a, b) => new Date(`1970-01-01T${a.time}`) - new Date(`1970-01-01T${b.time}`))
          .map(b => ({
            time: b.time,
            roi: b.roiCount ? b.roiSum / b.roiCount : 0,
            profit: b.profitSum
          }));

        setLiveChartData(aggregated);
        return;
      }

      const response = await api.get(`/api/minute-roi/chart-data/${investorId}`, {
        params: {
          hours: hoursParam
        }
      });

      const records = response.data?.data || [];
      setLiveChartData(formatRecords(records));
    } catch (error) {
      console.error('Error fetching live ROI timeseries:', error);
      setLiveChartData([]);
    } finally {
      setLiveChartLoading(false);
    }
  };

  const fetchBrokerInvestors = async () => {
    try {
      const response = await api.get('/api/broker-investors');
      setBrokerInvestors(response.data.brokerInvestors || []);
    } catch (error) {
      console.error('Error fetching broker-investor relationships:', error);
    }
  };

  // Load chart when investor selection or hours change
  useEffect(() => {
    if (selectedLiveInvestor) {
      fetchMinuteROIChart(selectedLiveInvestor, liveChartHours);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLiveInvestor, liveChartHours]);

  const fetchBrokers = async () => {
    try {
      const response = await api.get('/api/brokers');
      setBrokers(response.data.brokers || []);
    } catch (error) {
      console.error('Error fetching brokers:', error);
    }
  };

  const fetchInvestorHistory = async (investorId, startDate = null, endDate = null) => {
    setHistoryLoading(true);
    try {
      const params = {};
      if (startDate) {
        params.startDate = (startDate && typeof startDate.toISOString === 'function')
          ? startDate.toISOString()
          : startDate;
      }
      if (endDate) {
        params.endDate = (endDate && typeof endDate.toISOString === 'function')
          ? endDate.toISOString()
          : endDate;
      }

      const response = await api.get(`/api/roi/investor/${investorId}`, { params });
      setHistoryData(response.data.data.records || []);
    } catch (error) {
      console.error('Error fetching investor history:', error);
      showError('Failed to load history data');
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistoryModal = (investor) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    setHistoryInvestor(investor);
    setHistoryFilters({
      startDate: monthStart,
      endDate: today
    });
    setShowHistoryModal(true);
    fetchInvestorHistory(investor._id, monthStart, today);
  };

  const applyHistoryFilters = () => {
    if (historyInvestor) {
      fetchInvestorHistory(historyInvestor._id, historyFilters.startDate, historyFilters.endDate);
    }
  };

  const handleRegenerateProfits = async () => {
    if (!historyInvestor?._id || regeneratingProfits) return;

    const confirmed = await confirm({
      title: 'Regenerate profits',
      message:
        'This will recalculate historical ROI and broker commission from your investment start date using the current system logic (minute ROI → daily aggregates). It may take a minute. Continue?',
      confirmText: 'Regenerate',
      cancelText: 'Cancel',
    });
    if (!confirmed) return;

    try {
      setRegeneratingProfits(true);
      cancelEditingHistoryRecord();

      const startDateRaw = historyInvestor.startDate || historyInvestor.createdAt;
      await api.post(`/api/users/${historyInvestor._id}/recalculate-roi`, {
        ...(startDateRaw ? { startDate: new Date(startDateRaw).toISOString() } : {}),
      });

      showSuccess('Historical profits regenerated successfully');
      await fetchInvestorHistory(historyInvestor._id, historyFilters.startDate, historyFilters.endDate);

      // Keep the main Investors table + charts in sync (daily ROI, broker columns, live minute ROI, ROI chart)
      if (investors.length > 0) {
        await fetchDailyROI(investors);
        await fetchBrokerCommissions(investors);
        await fetchMinuteROI();
        await fetchChartData();
      }
    } catch (error) {
      console.error('Error regenerating profits:', error);
      showError(error.response?.data?.message || 'Failed to regenerate profits');
    } finally {
      setRegeneratingProfits(false);
    }
  };

  const startEditingHistoryRecord = (record) => {
    setEditingHistoryId(record._id);
    setEditingHistoryValues({
      roi_percentage: Number(record.roi_percentage || 0).toFixed(2),
      // Backend stores daily_return / broker_daily_return in AED (sum of minute AED profits)
      daily_profit_aed: Number(record.daily_return || 0).toFixed(2),
      broker_commission_aed: Number(record.broker_daily_return || 0).toFixed(2),
    });
  };

  const cancelEditingHistoryRecord = () => {
    setEditingHistoryId(null);
    setEditingHistoryValues({
      roi_percentage: '',
      daily_profit_aed: '',
      broker_commission_aed: '',
    });
  };

  const saveHistoryRecord = async (record) => {
    if (!historyInvestor?._id || !record?._id) return;

    const roiVal = Number(editingHistoryValues.roi_percentage);
    const dailyProfitAED = Number(editingHistoryValues.daily_profit_aed);
    const brokerCommissionAED = Number(editingHistoryValues.broker_commission_aed);

    if (!Number.isFinite(roiVal) || roiVal < 0) {
      showError('ROI % must be a valid non-negative number');
      return;
    }
    if (!Number.isFinite(dailyProfitAED) || dailyProfitAED < 0) {
      showError('Daily profit must be a valid non-negative number');
      return;
    }
    if (!Number.isFinite(brokerCommissionAED) || brokerCommissionAED < 0) {
      showError('Broker commission must be a valid non-negative number');
      return;
    }

    try {
      setSavingHistory(true);
      await api.put(`/api/roi/investor/${historyInvestor._id}/${record._id}`, {
        roi_percentage: roiVal,
        daily_profit_aed: dailyProfitAED,
        broker_commission_aed: brokerCommissionAED,
      });

      // Refresh table immediately with edited values for better UX (amounts are AED).
      setHistoryData((prev) =>
        prev.map((item) =>
          item._id === record._id
            ? {
                ...item,
                roi_percentage: roiVal,
                daily_return: dailyProfitAED,
                monthly_return: dailyProfitAED * 30,
                broker_daily_return: brokerCommissionAED,
                broker_monthly_return: brokerCommissionAED * 30,
                daily_balance: dailyProfitAED - brokerCommissionAED,
                monthly_balance: dailyProfitAED * 30 - brokerCommissionAED * 30,
              }
            : item
        )
      );

      showSuccess('Earning history updated successfully');
      cancelEditingHistoryRecord();
      // Re-fetch from backend to guarantee final source-of-truth sync.
      await fetchInvestorHistory(historyInvestor._id, historyFilters.startDate, historyFilters.endDate);
    } catch (error) {
      console.error('Error updating earning history:', error);
      showError(error.response?.data?.message || 'Failed to update earning history');
    } finally {
      setSavingHistory(false);
    }
  };

  // Generate random password that meets requirements (min 8 chars, uppercase, lowercase, number)
  const generatePassword = () => {
    const length = 12;
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const special = '!@#$%^&*';
    const allChars = lowercase + uppercase + numbers + special;

    // Ensure password has at least one of each required type
    let password = '';
    password += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
    password += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));

    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }

    // Shuffle the password to avoid predictable pattern
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setFormData({ ...formData, password: newPassword });
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(formData.password);
      showSuccess('Password copied to clipboard');
    } catch (error) {
      showError('Failed to copy password');
    }
  };

  const handleOpenModal = () => {
    setFormData({
      fullName: '',
      email: '',
      password: '',
      invested_amount: 0,
      roi_min: 0.5,
      roi_max: 1.5,
      startDate: null,
      broker_id: ''
    });
    setShowModal(true);
  };

  const handleOpenBrokerModal = () => {
    setBrokerFormData({
      name: '',
      email: '',
      password: '',
      startDate: null
    });
    setShowBrokerModal(true);
  };

  const handleGenerateBrokerPassword = () => {
    const newPassword = generatePassword();
    setBrokerFormData({ ...brokerFormData, password: newPassword });
  };

  const handleCopyBrokerPassword = async () => {
    try {
      await navigator.clipboard.writeText(brokerFormData.password);
      showSuccess('Password copied to clipboard');
    } catch (error) {
      showError('Failed to copy password');
    }
  };

  const handleCreateBroker = async (e) => {
    e.preventDefault();
    try {
      // Validate password meets requirements
      if (!brokerFormData.password || brokerFormData.password.length < 8) {
        showError('Password must be at least 8 characters long');
        return;
      }
      if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(brokerFormData.password)) {
        showError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
        return;
      }

      const response = await api.post('/api/brokers', brokerFormData);
      const newBroker = response.data.broker;

      showSuccess('Broker created successfully');
      setShowBrokerModal(false);

      // Fetch updated broker list first
      await fetchBrokers();

      // Auto-select the newly created broker using functional update to avoid stale state
      setFormData(prevData => ({
        ...prevData,
        broker_id: newBroker._id,
        broker_min_roi: newBroker.min_roi || newBroker.minRoi || 0.5,
        broker_max_roi: newBroker.max_roi || newBroker.maxRoi || 1.5
      }));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Failed to create broker';
      showError(errorMessage);
    }
  };

  const handleEdit = (investor) => {
    setEditingInvestor(investor);
    // Parse startDate as local date to avoid timezone issues
    let startDate = null;
    if (investor.startDate) {
      const dateStr = investor.startDate;
      // If it's a date string in YYYY-MM-DD format, parse as local date
      const dateMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const year = parseInt(dateMatch[1], 10);
        const month = parseInt(dateMatch[2], 10) - 1; // Month is 0-indexed
        const day = parseInt(dateMatch[3], 10);
        startDate = new Date(year, month, day);
      } else {
        // Fallback to standard parsing
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          startDate = date;
        }
      }
    }

    // Get ROI values from BrokerInvestor relationship if exists, otherwise from investor
    let roi_min = investor.roi_min || 0.5;
    let roi_max = investor.roi_max || 1.5;
    const relationship = brokerInvestors.find(
      rel => rel.investor_id?._id === investor._id || rel.investor_id === investor._id
    );
    if (relationship && relationship.roi_min !== undefined && relationship.roi_max !== undefined) {
      roi_min = relationship.roi_min || 0.5;
      roi_max = relationship.roi_max || 1.5;
    }

    setFormData({
      firstName: investor.firstName || '',
      lastName: investor.lastName || '',
      email: investor.email || '',
      password: '', // Don't pre-fill password
      invested_amount: investor.invested_amount ? parseFloat(investor.invested_amount.toFixed(2)) : 0,
      balance_usdt: investor.balance_usdt ? parseFloat(investor.balance_usdt.toFixed(2)) : 0,
      roi_min: roi_min,
      roi_max: roi_max,
      startDate: startDate,
      broker_id: relationship?.broker_id?._id || relationship?.broker_id || '',
      broker_min_roi: roi_min,
      broker_max_roi: roi_max
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingInvestor) return;

    try {
      const previousInvestedAmount = Number(editingInvestor.invested_amount || 0);
      const nextInvestedAmount = Number(formData.invested_amount || 0);
      const investedAmountChanged = Math.abs(previousInvestedAmount - nextInvestedAmount) > 0.000001;

      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        roi_min: formData.roi_min || 0,
        roi_max: formData.roi_max || 0
      };

      // Keep the regular direct update path for fields not tied to recalculation.
      if (!investedAmountChanged) {
        updateData.invested_amount = formData.invested_amount || 0;
        updateData.balance_usdt = formData.balance_usdt || 0;
      }

      // Add startDate if it exists in formData
      // Format as YYYY-MM-DD to avoid timezone issues
      if (formData.startDate !== undefined) {
        if (formData.startDate) {
          if (formData.startDate instanceof Date) {
            const year = formData.startDate.getFullYear();
            const month = String(formData.startDate.getMonth() + 1).padStart(2, '0');
            const day = String(formData.startDate.getDate()).padStart(2, '0');
            updateData.startDate = `${year}-${month}-${day}`;
          } else if (typeof formData.startDate === 'string') {
            // If it's already a string, use it directly (should be YYYY-MM-DD)
            updateData.startDate = formData.startDate;
          }
        } else {
          updateData.startDate = null;
        }
      }

      // Only update password if provided
      if (formData.password) {
        updateData.password = formData.password;
      }

      await api.put(`/api/users/${editingInvestor._id}`, updateData);

      // Sync broker relationship (create/update/delete)
      const existingRelationship = brokerInvestors.find(
        rel => rel.investor_id?._id === editingInvestor._id || rel.investor_id === editingInvestor._id
      );

      const hasSelectedBroker = !!formData.broker_id;
      const existingBrokerId = existingRelationship
        ? (existingRelationship.broker_id?._id || existingRelationship.broker_id || '').toString()
        : '';
      const selectedBrokerId = (formData.broker_id || '').toString();
      const brokerChanged = !!existingRelationship && !!selectedBrokerId && existingBrokerId !== selectedBrokerId;

      const brokerPayload = {
        broker_id: selectedBrokerId,
        investor_id: editingInvestor._id,
        roi_min: formData.roi_min || formData.broker_min_roi || 0,
        roi_max: formData.roi_max || formData.broker_max_roi || 0,
        broker_min_roi: formData.broker_min_roi ?? formData.roi_min ?? 0,
        broker_max_roi: formData.broker_max_roi ?? formData.roi_max ?? 0
      };

      let brokerRelationChanged = false;

      if (hasSelectedBroker) {
        // Re-create relationship if broker changed to avoid stale/immutable relation edge cases.
        if (brokerChanged) {
          await api.delete(`/api/broker-investors/${existingRelationship._id}`);
          await api.post('/api/broker-investors', brokerPayload);
          brokerRelationChanged = true;
        } else if (existingRelationship) {
          await api.put(`/api/broker-investors/${existingRelationship._id}`, brokerPayload);
          brokerRelationChanged = true;
        } else {
          await api.post('/api/broker-investors', brokerPayload);
          brokerRelationChanged = true;
        }
      } else if (existingRelationship) {
        await api.delete(`/api/broker-investors/${existingRelationship._id}`);
        brokerRelationChanged = true;
      }

      let adjustmentSummary = null;
      if (investedAmountChanged) {
        const adjustmentResponse = await api.post(`/api/users/${editingInvestor._id}/adjust-investment`, {
          mode: 'set',
          amount: nextInvestedAmount,
          date: new Date(),
          note: 'Quick investment edit from investor update form'
        });
        adjustmentSummary = adjustmentResponse.data?.recalculation || null;
      } else if (brokerRelationChanged) {
        const recalculationResponse = await api.post(`/api/users/${editingInvestor._id}/recalculate-roi`, {
          startDate: formData.startDate ?
            (formData.startDate instanceof Date
              ? `${formData.startDate.getFullYear()}-${String(formData.startDate.getMonth() + 1).padStart(2, '0')}-${String(formData.startDate.getDate()).padStart(2, '0')}`
              : formData.startDate)
            : undefined
        });
        adjustmentSummary = recalculationResponse.data?.recalculation || null;
      }

      if (adjustmentSummary) {
        showSuccess(
          `Investor updated. Recalculated (${adjustmentSummary.daily_roi_recalculated || 0} daily, ${adjustmentSummary.minute_roi_updated || 0} minute, ${adjustmentSummary.broker_daily_commission_recalculated || 0} broker commission days).`
        );
      } else {
        showSuccess('Investor updated successfully');
      }

      setShowEditModal(false);
      setEditingInvestor(null);
      await fetchInvestors();
      await fetchBrokerInvestors(); // Refresh broker-investor relationships to get updated ROI
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update investor');
    }
  };

  const handleDeactivate = async (investor) => {
    const confirmed = await confirm({
      title: investor.active !== false ? 'Deactivate Investor' : 'Activate Investor',
      message: `Are you sure you want to ${investor.active !== false ? 'deactivate' : 'activate'} ${investor.firstName} ${investor.lastName}?`,
      confirmText: investor.active !== false ? 'Deactivate' : 'Activate',
      cancelText: 'Cancel',
      variant: investor.active !== false ? 'warning' : 'primary'
    });

    if (!confirmed) return;

    try {
      await api.put(`/api/users/${investor._id}`, {
        active: investor.active === false
      });
      showSuccess(`Investor ${investor.active !== false ? 'deactivated' : 'activated'} successfully`);
      fetchInvestors();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update investor status');
    }
  };

  const handleDelete = async (investor) => {
    const confirmed = await confirm({
      title: 'Delete Investor',
      message: `Are you sure you want to delete ${investor.firstName} ${investor.lastName}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/users/${investor._id}`);
      showSuccess('Investor deleted successfully');
      fetchInvestors();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to delete investor');
    }
  };

  const handleBulkDelete = async () => {
    if (checkedInvestors.size === 0) {
      showError('Please select at least one investor');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Multiple Investors',
      message: `Are you sure you want to delete ${checkedInvestors.size} investor(s)? This action cannot be undone.`,
      confirmText: 'Delete All',
      cancelText: 'Cancel',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const deletePromises = Array.from(checkedInvestors).map(id => api.delete(`/api/users/${id}`));
      await Promise.all(deletePromises);
      showSuccess(`${checkedInvestors.size} investor(s) deleted successfully`);
      setCheckedInvestors(new Set());
      fetchInvestors();
    } catch (error) {
      showError('Failed to delete one or more investors');
    }
  };

  const toggleInvestorCheck = (investorId) => {
    const newSet = new Set(checkedInvestors);
    if (newSet.has(investorId)) {
      newSet.delete(investorId);
    } else {
      newSet.add(investorId);
    }
    setCheckedInvestors(newSet);
  };

  const toggleAllInvestorsCheck = () => {
    if (checkedInvestors.size === filteredInvestors.length) {
      setCheckedInvestors(new Set());
    } else {
      setCheckedInvestors(new Set(filteredInvestors.map(inv => inv._id)));
    }
  };

  // Investment handlers
  const handleOpenAddInvestment = (investor) => {
    setSelectedInvestorForInvestment(investor);
    setNewInvestmentForm({
      amount: '',
      date: new Date(),
      note: ''
    });
    setShowInvestmentModal(true);
  };

  const handleAddInvestment = async () => {
    if (!selectedInvestorForInvestment) return;

    if (!newInvestmentForm.amount || parseFloat(newInvestmentForm.amount) <= 0) {
      showError('Please enter a valid investment amount');
      return;
    }

    try {
      setInvestmentLoading(true);
      const response = await api.post(`/api/users/${selectedInvestorForInvestment._id}/add-investment`, {
        amount: parseFloat(newInvestmentForm.amount),
        date: newInvestmentForm.date,
        note: newInvestmentForm.note
      });
      const recalculation = response.data?.recalculation || {};
      const snapshots = response.data?.snapshots || {};

      setShowInvestmentModal(false);
      setNewInvestmentForm({ amount: '', date: new Date(), note: '' });
      setSelectedInvestorForInvestment(null);

      // Refresh investor list to show updated invested_amount, balance, and recalculated ROI
      await fetchInvestors();

      const amountText = `$${parseFloat(newInvestmentForm.amount).toLocaleString()}`;
      const dailyCount = recalculation.daily_roi_recalculated || 0;
      const minuteCount = recalculation.minute_roi_updated || 0;
      const hourlyCount = recalculation.hourly_profit_recalculated || 0;
      const weeklyCount = recalculation.weekly_profit_recalculated || 0;
      const monthlyCount = recalculation.monthly_profit_recalculated || 0;
      const prevInvested = Number(response.data?.investment?.previous_invested_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const prevBalance = Number(snapshots.balance_snapshot_aed || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      if (recalculation.status === 'failed') {
        showSuccess(`Investment of ${amountText} added. ROI recalculation partially failed: ${recalculation.error || 'unknown error'}`);
      } else {
        showSuccess(
          `Investment of ${amountText} added. Previous invested: ${prevInvested} USDT, previous earnings balance: AED ${prevBalance}. ` +
          `Recalculated ${minuteCount} minute, ${hourlyCount} hourly, ${dailyCount} daily, ${weeklyCount} weekly, ${monthlyCount} monthly buckets.`
        );
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to add investment');
    } finally {
      setInvestmentLoading(false);
    }
  };

  const handleViewInvestmentHistory = async (investor) => {
    setSelectedInvestorForInvestment(investor);
    setShowInvestmentHistoryModal(true);
    setInvestmentLoading(true);

    try {
      const response = await api.get(`/api/users/${investor._id}/investment-history`);
      setInvestmentData(response.data.history || []);
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to load investment history');
      setInvestmentData([]);
    } finally {
      setInvestmentLoading(false);
    }
  };

  const handleOpenAdjustInvestment = (investor) => {
    setSelectedInvestorForInvestment(investor);
    setAdjustInvestmentForm({
      mode: 'reduce',
      amount: '',
      date: new Date(),
      note: ''
    });
    setShowAdjustInvestmentModal(true);
  };

  const handleAdjustInvestment = async () => {
    if (!selectedInvestorForInvestment) return;

    if (adjustInvestmentForm.amount === '' || Number(adjustInvestmentForm.amount) < 0) {
      showError('Please enter a valid amount');
      return;
    }

    if (adjustInvestmentForm.mode === 'reduce' && Number(adjustInvestmentForm.amount) <= 0) {
      showError('Reduction amount must be greater than 0');
      return;
    }

    try {
      setAdjustInvestmentLoading(true);

      const response = await api.post(`/api/users/${selectedInvestorForInvestment._id}/adjust-investment`, {
        mode: adjustInvestmentForm.mode,
        amount: Number(adjustInvestmentForm.amount),
        date: adjustInvestmentForm.date,
        note: adjustInvestmentForm.note
      });

      const recalculation = response.data?.recalculation || {};
      const snapshots = response.data?.snapshots || {};
      const dailyCount = recalculation.daily_roi_recalculated || 0;
      const minuteCount = recalculation.minute_roi_updated || 0;
      const prevBalance = Number(snapshots.balance_snapshot_aed || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      setShowAdjustInvestmentModal(false);
      setAdjustInvestmentForm({ mode: 'reduce', amount: '', date: new Date(), note: '' });

      await fetchInvestors();

      if (showInvestmentHistoryModal) {
        await handleViewInvestmentHistory(selectedInvestorForInvestment);
      }

      showSuccess(
        `Investment ${adjustInvestmentForm.mode === 'reduce' ? 'reduced' : 'updated'} successfully. ` +
        `Previous balance: AED ${prevBalance}. ROI updated (${dailyCount} daily, ${minuteCount} minute records).`
      );
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to adjust investment');
    } finally {
      setAdjustInvestmentLoading(false);
    }
  };

  const handleViewInvestmentSnapshot = (record) => {
    setSelectedInvestmentSnapshot(record);
    setShowInvestmentSnapshotModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (createInvestorLoading) return; // Prevent duplicate submissions
    try {
      // Split full name into first and last name
      const nameParts = formData.fullName.trim().split(/\s+/).filter(part => part.length > 0);
      if (nameParts.length === 0) {
        showError('Full name is required');
        return;
      }
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || firstName; // Use firstName as lastName if only one word provided

      // Validate password meets requirements
      if (!formData.password || formData.password.length < 8) {
        showError('Password must be at least 8 characters long');
        return;
      }
      if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
        showError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
        return;
      }

      setCreateInvestorLoading(true);

      // Create user with investor role (using admin endpoint)
      // If start date is in the past, historical ROI and commission records will be auto-generated
      const response = await api.post('/api/users', {
        firstName,
        lastName,
        email: formData.email,
        password: formData.password,
        role: 'investor',
        invested_amount: formData.invested_amount || 0,
        roi_min: formData.roi_min || 0,
        roi_max: formData.roi_max || 0,
        broker_min_roi: formData.broker_min_roi || 0,
        broker_max_roi: formData.broker_max_roi || 0,
        startDate: formData.startDate
          ? (formData.startDate instanceof Date
            ? (() => {
              const year = formData.startDate.getFullYear();
              const month = String(formData.startDate.getMonth() + 1).padStart(2, '0');
              const day = String(formData.startDate.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            })()
            : formData.startDate)
          : null,
        broker_id: formData.broker_id || null
      });

      // Show notification if historical records were generated
      if (response.data.historicalRecords?.roiRecords) {
        showSuccess(
          `Investor created successfully with ${response.data.historicalRecords.roiRecords.dailyRecords} days of historical ROI data`
        );
      } else {
        showSuccess('Investor created successfully');
      }

      const userId = response.data.user.id;

      // If broker is selected, create broker-investor relationship with broker ROI values
      if (formData.broker_id) {
        // Note: Broker model no longer has min_roi/max_roi fields
        // They are now stored only in BrokerInvestor model per relationship

        // Create/Update broker-investor relationship with investor's ROI and broker ROI values
        try {
          await api.post('/api/broker-investors', {
            broker_id: formData.broker_id,
            investor_id: userId,
            roi_min: formData.roi_min || 0,
            roi_max: formData.roi_max || 0,
            broker_min_roi: formData.broker_min_roi || 0,
            broker_max_roi: formData.broker_max_roi || 0
          });
        } catch (brokerError) {
          // Handle error but don't fail the whole flow
          console.error('Error creating broker-investor relationship:', brokerError);
        }
      }

      showSuccess('Investor created successfully');

      // If start date is in the past, trigger historical ROI calculation
      if (formData.startDate) {
        const startDate = formData.startDate instanceof Date
          ? formData.startDate
          : new Date(formData.startDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (startDate < today) {
          showSuccess('Calculating historical ROI data...');
          try {
            const formattedStartDate = formData.startDate instanceof Date
              ? (() => {
                const year = formData.startDate.getFullYear();
                const month = String(formData.startDate.getMonth() + 1).padStart(2, '0');
                const day = String(formData.startDate.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              })()
              : formData.startDate;

            await api.post('/api/scheduler/backfill-investor-roi', {
              investorId: userId,
              startDate: formattedStartDate
            });
            showSuccess('Historical ROI calculated successfully');
          } catch (error) {
            console.error('Error calculating historical ROI:', error);
            showError('Investor created, but failed to calculate historical ROI');
          }
        }
      }

      setShowModal(false);
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        password: '',
        invested_amount: 0,
        roi_min: 0.5,
        roi_max: 1.5,
        startDate: null,
        broker_id: '',
        broker_min_roi: 0.5,
        broker_max_roi: 1.5
      });
      fetchInvestors();
      fetchBrokerInvestors();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to create investor');
    } finally {
      setCreateInvestorLoading(false);
    }
  };

  const fetchInvestorDetails = async (userId) => {
    setInvestorDetails({ portfolio: null, transactions: [], payouts: [], roiHistory: [], loading: true });
    try {
      const [portfolioRes, transactionsRes, payoutsRes, roiRes] = await Promise.all([
        api.get(`/api/users/${userId}/portfolio`).catch(() => ({ data: { portfolio: null } })),
        api.get(`/api/users/${userId}/transactions?limit=50`).catch(() => ({ data: { transactions: [] } })),
        api.get(`/api/payouts?userId=${userId}`).catch(() => ({ data: { payouts: [] } })),
        api.get(`/api/roi/history?limit=30`).catch(() => ({ data: { data: { records: [] } } }))
      ]);

      setInvestorDetails({
        portfolio: portfolioRes.data.portfolio,
        transactions: transactionsRes.data.transactions || [],
        payouts: payoutsRes.data.payouts || [],
        roiHistory: roiRes.data.data?.records || [],
        loading: false
      });
    } catch (error) {
      console.error('Error fetching investor details:', error);
      setInvestorDetails({ portfolio: null, transactions: [], payouts: [], roiHistory: [], loading: false });
    }
  };

  const handleInvestorClick = async (investor) => {
    setSelectedInvestor(investor);
    setShowDrawer(true);
    await fetchInvestorDetails(investor._id);
    await fetchAllocatedProfits(investor._id);
  };

  const fetchAllocatedProfits = async (investorId) => {
    try {
      const response = await api.get(`/api/portfolio/investor/${investorId}/allocated-profits`, {
        params: { period: allocatedProfitsPeriod }
      });
      setInvestorDetails(prev => ({
        ...prev,
        allocatedProfits: response.data.data
      }));
    } catch (error) {
      console.error('Error fetching allocated profits:', error);
      // Silently fail - this is optional data
    }
  };

  const getRoleBadge = (role) => {
    const variants = {
      investor: 'primary',
      admin: 'secondary',
      broker: 'info',
      trader: 'primary',
    };
    return <Badge variant={variants[role] || 'neutral'} size="sm">{role}</Badge>;
  };


  // Filter and sort investors
  const filteredInvestors = investors
    .filter(inv => {
      const searchLower = filters.search.toLowerCase();
      const name = `${inv.firstName || ''} ${inv.lastName || ''}`.toLowerCase();
      const email = (inv.email || '').toLowerCase();
      return name.includes(searchLower) || email.includes(searchLower);
    })
    .map(inv => ({
      ...inv,
      // Use balance_aed from backend if available, or calculate it
      balance: inv.balance_aed || (inv.balance_usdt || 0) * (parseFloat(import.meta.env.VITE_USD_TO_AED_RATE) || 3.662)
    }))
    .sort((a, b) => {
      if (filters.sortBy === 'name') {
        const nameA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
        const nameB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
        return nameA.localeCompare(nameB);
      } else if (filters.sortBy === 'invested') {
        const valA = a.invested_amount_aed || (a.invested_amount || 0) * rate;
        const valB = b.invested_amount_aed || (b.invested_amount || 0) * rate;
        return valB - valA;
      }
      return 0;
    });

  // Helper function to get ROI range for an investor
  const getInvestorROIRange = (investorId) => {
    // First check broker-investor relationship
    const relationship = brokerInvestors.find(
      rel => rel.investor_id?._id === investorId || rel.investor_id === investorId
    );
    if (relationship && relationship.roi_min !== undefined && relationship.roi_max !== undefined) {
      return `${(relationship.roi_min || 0).toFixed(2)}% - ${(relationship.roi_max || 0).toFixed(2)}%`;
    }

    // Fallback to investor's own ROI range (for investors without brokers)
    const investor = investors.find(inv => inv._id === investorId);
    if (investor && investor.roi_min !== undefined && investor.roi_max !== undefined &&
      investor.roi_min !== 0 && investor.roi_max !== 0) {
      return `${(investor.roi_min || 0).toFixed(2)}% - ${(investor.roi_max || 0).toFixed(2)}%`;
    }

    return 'N/A';
  };

  // Helper function to calculate profit (Total Days * Today's Profit - Payouts)
  const calculateProfit = (investor) => {
    const roiData = dailyROIData[investor._id];
    const roi = roiData?.latest;
    const live = minuteROIData[investor._id];
    const liveDaily = live ? (live.profit_per_minute || 0) * 60 * 24 : 0;
    const dailyProfit = roi?.daily_return ?? liveDaily;
    // DailyROI.daily_return and MinuteROI profit_per_minute are both AED
    const dailyProfitAED = dailyProfit;

    // Calculate total days from start date till today
    const startDateVal = investor.startDate || investor.createdAt || investor.created_at;
    let totalDays = 0;
    if (startDateVal) {
      const startDate = new Date(startDateVal);
      const today = new Date();
      // Use setHours(0,0,0,0) to compare dates only
      const startDay = new Date(startDate).setHours(0, 0, 0, 0);
      const todayDay = new Date(today).setHours(0, 0, 0, 0);
      const diffTime = todayDay - startDay;
      totalDays = diffTime >= 0 ? Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1 : 0;
    }

    const aggregatedReturn = roiData?.allRecords?.reduce(
      (sum, record) => sum + (record.daily_return || 0),
      0
    ) || 0;

    // Prefer true historical aggregation when available (handles investment edits/reductions over time).
    const netProfitAED = (roiData?.allRecords?.length > 0)
      ? aggregatedReturn
      : (totalDays * dailyProfitAED);

    // Subtract payouts (these are already recorded in AED)
    const investorIdStr = String(investor._id);
    const payoutItem = payoutData.find(item =>
      String(item.investor_id) === investorIdStr
    );
    const totalPayoutsAED = (payoutItem?.total_payout || 0);

    return netProfitAED - totalPayoutsAED;
  };

  // Calculate KPIs
  const totalInvestors = investors.length;
  const rate = parseFloat(import.meta.env.VITE_USD_TO_AED_RATE) || 3.662;
  const totalInvestedAED = investors.reduce((sum, inv) => sum + (inv.invested_amount_aed || (inv.invested_amount || 0) * rate), 0);
  const activeInvestors = investors.filter(inv => inv.active !== false).length;

  // Calculate Total Profit & Balance for KPIs
  const totalNetProfitAED = investors.reduce((sum, inv) => sum + calculateProfit(inv), 0);
  const totalBalanceAED = investors.reduce((sum, inv) => {
    const profit = calculateProfit(inv);
    const investedAED = inv.invested_amount_aed || (inv.invested_amount || 0) * rate;

    return sum + (investedAED + profit);
  }, 0);

  const tableColumns = [
    {
      key: 'investor',
      label: 'INVESTOR',
      sortValue: (investor) => {
        return investor.firstName && investor.lastName
          ? `${investor.firstName} ${investor.lastName}`
          : investor.email || 'N/A';
      },
      render: (investor) => {
        const profit = calculateProfit(investor);
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-600 dark:bg-primary-700 flex items-center justify-center text-white font-semibold flex-shrink-0">
              <UserCircle size={20} />
            </div>
            <div className="flex flex-col">
              <button
                onClick={() => handleInvestorClick(investor)}
                className="text-left font-medium text-primary-900 dark:text-primary-50 hover:text-primary-600 dark:hover:text-primary-400 hover:underline transition-colors cursor-pointer"
              >
                {investor.firstName && investor.lastName
                  ? `${investor.firstName} ${investor.lastName}`
                  : investor.email || 'N/A'}
              </button>
              <span className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
                {investor.email || 'N/A'}
              </span>
            </div>
          </div>
        );
      },
      exportRender: (investor) => {
        return investor.firstName && investor.lastName
          ? `${investor.firstName} ${investor.lastName}`
          : investor.email || 'N/A';
      },
    },
    {
      key: 'investment',
      label: 'INVESTMENT',
      sortValue: (investor) => investor.invested_amount_aed || (investor.invested_amount || 0) * (parseFloat(import.meta.env.VITE_USD_TO_AED_RATE) || 3.662),
      render: (investor) => {
        const rate = parseFloat(import.meta.env.VITE_USD_TO_AED_RATE) || 3.662;
        const investedAED = investor.invested_amount_aed || (investor.invested_amount || 0) * rate;
        const investedUSD = investor.invested_amount || (investedAED / rate);
        return (
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-primary-900 dark:text-primary-50 flex items-center gap-1">
              <DirhamSymbol size={14} inline />
              {investedAED.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400">
              ${investedUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        );
      },
      exportRender: (investor) => (investor.invested_amount_aed || (investor.invested_amount || 0) * (parseFloat(import.meta.env.VITE_USD_TO_AED_RATE) || 3.662)).toFixed(2),
    },
    {
      key: 'profit',
      label: 'DAILY PROFIT',
      sortValue: (investor) => {
        const roiData = dailyROIData[investor._id];
        const roi = roiData?.latest;
        const live = minuteROIData[investor._id];
        const liveDaily = live ? (live.profit_per_minute || 0) * 60 * 24 : 0;
        return roi?.daily_return || liveDaily || 0;
      },
      render: (investor) => {
        const roiData = dailyROIData[investor._id];
        const roi = roiData?.latest;
        const live = minuteROIData[investor._id];
        const liveDaily = live ? (live.profit_per_minute || 0) * 60 * 24 : 0;
        const dailyProfit = roi?.daily_return ?? liveDaily;
        const monthlyProfit = roi?.monthly_return ?? (liveDaily * 30);

        // daily_return / monthly_return from API are AED (same as live profit_per_minute × minutes)
        const dailyProfitAED = dailyProfit;
        const monthlyProfitAED = monthlyProfit;

        return (
          <button
            onClick={() => openHistoryModal(investor)}
            className="flex flex-col gap-1 text-left hover:opacity-80 transition-opacity cursor-pointer"
            title="Click to view full ROI history"
          >
            <div className="flex items-center gap-1">
              <TrendingUp size={14} className="text-success-600 dark:text-success-400" />
              <span className="font-semibold text-primary-900 dark:text-primary-50 flex items-center gap-1">
                <DirhamSymbol size={14} inline />
                {dailyProfitAED.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <span className="text-xs text-primary-600 dark:text-primary-400">
              Monthly: <span className="flex items-center gap-1 inline-flex font-medium">
                <DirhamSymbol size={12} inline />
                {monthlyProfitAED.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </span>
          </button>
        );
      },
      exportRender: (investor) => {
        const roiData = dailyROIData[investor._id];
        const roi = roiData?.latest;
        const live = minuteROIData[investor._id];
        const liveDaily = live ? (live.profit_per_minute || 0) * 60 * 24 : 0;
        const dailyProfit = roi?.daily_return ?? liveDaily;
        return (dailyProfit || 0).toFixed(2);
      },
    },
    {
      key: 'balance',
      label: 'BALANCE',
      sortValue: (investor) => calculateProfit(investor),
      render: (investor) => {
        const conversionRate = parseFloat(import.meta.env.VITE_USD_TO_AED_RATE) || 3.662;
        const netBalanceAED = calculateProfit(investor);
        const netBalanceUSDT = netBalanceAED / conversionRate;

        // Payout total is already in AED from the backend
        const investorIdStr = String(investor._id);
        const payoutItem = payoutData.find(item =>
          String(item.investor_id) === investorIdStr
        );
        const totalPayoutsAED = (payoutItem?.total_payout || 0);

        return (
          <button
            onClick={() => openHistoryModal(investor)}
            className="flex flex-col gap-1 text-left hover:opacity-80 transition-opacity cursor-pointer"
            title="Click to view full history"
          >
            <div className="flex items-center gap-1 font-semibold text-primary-900 dark:text-primary-50">
              <DirhamSymbol size={14} inline />
              {Number(netBalanceAED || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <span className="text-xs text-primary-600 dark:text-primary-400">
              ${Number(netBalanceUSDT || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
            </span>
            {totalPayoutsAED > 0 && (
              <div className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                <span>-</span>
                <DirhamSymbol size={10} inline />
                {totalPayoutsAED.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <span className="ml-1">from payouts</span>
              </div>
            )}
          </button>
        );
      },
      exportRender: (investor) => {
        const roiData = dailyROIData[investor._id];
        const aggregatedReturn = roiData?.allRecords?.reduce(
          (sum, record) => sum + (record.daily_return || 0),
          0
        ) || 0;
        const rate = parseFloat(import.meta.env.VITE_USD_TO_AED_RATE) || 3.662;
        const balanceAED = aggregatedReturn;
        const balanceUSDT = rate ? aggregatedReturn / rate : aggregatedReturn;
        return `${balanceAED.toFixed(2)} AED (${balanceUSDT.toFixed(2)} USDT)`;
      },
    },
    {
      key: 'broker_commission',
      label: 'BROKER COMMISSION',
      sortValue: (investor) => brokerCommissionData[investor._id]?.total_aed || 0,
      render: (investor) => {
        const commission = brokerCommissionData[investor._id] || {};
        const netCommissionAED = Number(commission.total_aed || 0);
        const netCommissionUSDT = Number(commission.total_usdt || 0);
        const todayCommissionAED = Number(commission.today_commission_aed || 0);
        const brokerDays = Number(commission.broker_days || 0);

        // Show zero if no commission
        if (netCommissionUSDT === 0) {
          return (
            <span className="text-primary-600 dark:text-primary-400 text-sm">0.00</span>
          );
        }

        return (
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1 font-semibold text-orange-600 dark:text-orange-400">
              <DirhamSymbol size={14} inline />
              {netCommissionAED.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-xs text-primary-600 dark:text-primary-400">
              ${netCommissionUSDT.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
            </span>
            <span className="text-xs text-success-600 dark:text-success-400">
              Today: <DirhamSymbol size={10} inline /> {todayCommissionAED.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-xs text-primary-600 dark:text-primary-400">
              Days: {brokerDays}
            </span>
          </div>
        );
      },
      exportRender: (investor) => {
        const netCommissionAED = Number(brokerCommissionData[investor._id]?.total_aed || 0);
        return `${netCommissionAED.toFixed(2)} AED`;
      },
    },
    {
      key: 'roi_range',
      label: 'ROI RANGE',
      sortValue: (investor) => getInvestorROIRange(investor._id),
      render: (investor) => {
        const roiRange = getInvestorROIRange(investor._id);
        return (
          <span className="text-primary-900 dark:text-primary-50 font-medium">
            {roiRange}
          </span>
        );
      },
      exportRender: (investor) => getInvestorROIRange(investor._id),
    },
    {
      key: 'daily_roi',
      label: 'TODAY\'S ROI',
      sortValue: (investor) => {
        const roiData = dailyROIData[investor._id];
        const roi = roiData?.latest;
        return roi?.roi_percentage || 0;
      },
      render: (investor) => {
        const roiData = dailyROIData[investor._id];
        const roi = roiData?.latest;
        if (!roi) {
          return <span className="text-primary-600 dark:text-primary-400 text-sm">No data</span>;
        }
        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Zap size={14} className="text-yellow-500" />
              <span className="font-semibold text-primary-900 dark:text-primary-50">
                {(roi.roi_percentage || 0).toFixed(2)}%
              </span>
            </div>
            <div className="flex items-center gap-1 text-sm text-success-600 dark:text-success-400">
              <DirhamSymbol size={12} inline />
              {(roi.daily_return || 0).toFixed(2)}
            </div>
          </div>
        );
      },
      exportRender: (investor) => {
        const roiData = dailyROIData[investor._id];
        const roi = roiData?.latest;
        return roi ? `${(roi.roi_percentage || 0).toFixed(2)}%` : 'No data';
      },
    },
    {
      key: 'live_roi',
      label: 'LIVE ROI (NOW)',
      sortValue: (investor) => {
        const roi = minuteROIData[investor._id];
        return roi?.roi_percentage || 0;
      },
      render: (investor) => {
        const roi = minuteROIData[investor._id];
        if (!roi) {
          return <span className="text-primary-600 dark:text-primary-400 text-sm">--</span>;
        }

        const profitPerMinute = roi.profit_per_minute || 0;
        const projectedDailyProfit = profitPerMinute * 60 * 24;

        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Activity size={14} className="text-success-600 dark:text-success-400 animate-pulse" />
              <span className="font-semibold text-primary-900 dark:text-primary-50">
                {(roi.roi_percentage || 0).toFixed(2)}%
              </span>
            </div>
            <div className="flex items-center gap-1 text-sm text-success-600 dark:text-success-400">
              <DirhamSymbol size={12} inline />
              {profitPerMinute.toFixed(4)}/min
            </div>
            <div className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
              <DirhamSymbol size={12} inline />
              {projectedDailyProfit.toFixed(2)} / day
            </div>
          </div>
        );
      },
      exportRender: (investor) => {
        const roi = minuteROIData[investor._id];
        if (!roi) return 'No data';
        const profitPerMinute = roi.profit_per_minute || 0;
        const projectedDailyProfit = profitPerMinute * 60 * 24;
        return `${(roi.roi_percentage || 0).toFixed(2)}% | ${profitPerMinute.toFixed(4)}/min | ${projectedDailyProfit.toFixed(2)}/day`;
      },
    },
    {
      key: 'status',
      label: 'STATUS',
      sortValue: (investor) => investor.active !== false ? 'Active' : 'Inactive',
      render: (investor) => (
        <Badge variant={investor.active !== false ? 'success' : 'error'} size="sm">
          {investor.active !== false ? 'Active' : 'Inactive'}
        </Badge>
      ),
      exportRender: (investor) => investor.active !== false ? 'Active' : 'Inactive',
    },
    {
      key: 'date',
      label: 'DATE',
      sortValue: (investor) => {
        const dateValue = investor.startDate || investor.createdAt || investor.created_at;
        return dateValue ? new Date(dateValue).getTime() : 0;
      },
      render: (investor) => {
        // Prioritize startDate over createdAt
        const dateValue = investor.startDate || investor.createdAt || investor.created_at;
        if (!dateValue) return 'N/A';
        try {
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) return 'N/A';
          return (
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-primary-600 dark:text-primary-400" />
              <span className="text-primary-900 dark:text-primary-50">
                {date.toISOString().split('T')[0]}
              </span>
            </div>
          );
        } catch (e) {
          return 'N/A';
        }
      },
    },
    {
      key: 'actions',
      label: 'ACTIONS',
      render: (investor) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleEdit(investor)}
            className="p-2 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 hover:bg-primary-100 dark:hover:bg-primary-800 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit size={18} />
          </button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleOpenAddInvestment(investor)}
            className="border-green-500 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20"
            title="Add Investment"
          >
            <Plus size={16} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewInvestmentHistory(investor)}
            className="border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            title="View Investment History"
          >
            <Wallet size={16} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleOpenAdjustInvestment(investor)}
            className="border-orange-500 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20"
            title="Adjust Investment"
          >
            <Edit size={16} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDeactivate(investor)}
            className="border-orange-500 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20"
          >
            {investor.active === false ? 'Activate' : 'Deactivate'}
          </Button>
          <button
            onClick={() => handleDelete(investor)}
            className="p-2 text-error-600 dark:text-error-400 hover:text-error-800 dark:hover:text-error-200 hover:bg-error-100 dark:hover:bg-error-900/20 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50 flex items-center gap-2">
            <UsersIcon size={28} className="text-primary-600 dark:text-primary-400" />
            Investors
          </h1>
          <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
            Manage and view all investor accounts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowBulkImportModal(true)}
            leftIcon={<Upload size={18} />}
            variant="outline"
          >
            Import Investors
          </Button>
          <Button
            onClick={handleOpenModal}
            leftIcon={<Plus size={18} />}
            variant="primary"
          >
            Add Investor
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
        <KPICard
          title="Total Investors"
          value={totalInvestors}
          icon={<UsersIcon size={24} />}
          color="primary"
        />
        <KPICard
          title="Total Invested (AED)"
          value={<div className="flex items-center gap-1"><DirhamSymbol size={20} inline /> {totalInvestedAED.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>}
          icon={<Wallet size={24} />}
          color="success"
        />
        <KPICard
          title="Total Net Profit (AED)"
          value={<div className="flex items-center gap-1"><DirhamSymbol size={20} inline /> {totalNetProfitAED.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>}
          icon={<TrendingUp size={24} />}
          color="info"
        />
        <KPICard
          title="Total Balance (AED)"
          value={<div className="flex items-center gap-1"><DirhamSymbol size={20} inline /> {totalBalanceAED.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>}
          icon={<DollarSign size={24} />}
          color="warning"
        />
      </div>

      {/* Live Minute ROI Data */}
      {Object.keys(minuteROIData).length > 0 && (
        <Card className="p-4 sm:p-6 border border-primary-200 dark:border-primary-700 bg-gradient-to-br from-primary-50 to-info-50 dark:from-primary-900/20 dark:to-info-900/20">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <Activity size={20} className="text-success-600 dark:text-success-400 animate-pulse" />
                <div>
                  <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50">Live ROI Data (Minute-Level)</h2>
                  <p className="text-sm text-primary-600 dark:text-primary-400">Real-time profit tracking updated every 30 seconds</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRefreshMinuteROI}
                    onChange={(e) => setAutoRefreshMinuteROI(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-primary-700 dark:text-primary-300">Auto-refresh</span>
                </label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchMinuteROI}
                  leftIcon={<RefreshCw size={16} className={minuteROILoading ? 'animate-spin' : ''} />}
                  disabled={minuteROILoading}
                >
                  {minuteROILoading ? 'Loading...' : 'Refresh'}
                </Button>
              </div>
            </div>

            {/* Live Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Average ROI */}
              <div className="bg-white dark:bg-primary-800 rounded-lg p-3 sm:p-4 border border-primary-200 dark:border-primary-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-primary-600 dark:text-primary-400 font-medium">AVG ROI (NOW)</p>
                    <p className="text-lg sm:text-2xl font-bold text-primary-900 dark:text-primary-50 mt-1">
                      {(Object.values(minuteROIData).reduce((sum, item) => sum + (item.roi_percentage || 0), 0) / Object.keys(minuteROIData).length).toFixed(2)}%
                    </p>
                  </div>
                  <Gauge size={24} className="text-primary-600 dark:text-primary-400" />
                </div>
              </div>

              {/* Total Profit Per Minute */}
              <div className="bg-white dark:bg-primary-800 rounded-lg p-3 sm:p-4 border border-success-200 dark:border-success-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-success-600 dark:text-success-400 font-medium">PROFIT/MIN</p>
                    <p className="text-lg sm:text-2xl font-bold text-success-600 dark:text-success-400 mt-1 flex items-center gap-1">
                      <DirhamSymbol size={16} inline />
                      {Object.values(minuteROIData).reduce((sum, item) => sum + (item.profit_per_minute || 0), 0).toFixed(2)}
                    </p>
                  </div>
                  <TrendingUp size={24} className="text-success-600 dark:text-success-400" />
                </div>
              </div>

              {/* Profit Per Hour */}
              <div className="bg-white dark:bg-primary-800 rounded-lg p-3 sm:p-4 border border-info-200 dark:border-info-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-info-600 dark:text-info-400 font-medium">PROFIT/HOUR</p>
                    <p className="text-lg sm:text-2xl font-bold text-info-600 dark:text-info-400 mt-1 flex items-center gap-1">
                      <DirhamSymbol size={16} inline />
                      {(Object.values(minuteROIData).reduce((sum, item) => sum + (item.profit_per_minute || 0), 0) * 60).toFixed(2)}
                    </p>
                  </div>
                  <Zap size={24} className="text-info-600 dark:text-info-400" />
                </div>
              </div>

              {/* Active Tracking */}
              <div className="bg-white dark:bg-primary-800 rounded-lg p-3 sm:p-4 border border-purple-200 dark:border-purple-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-purple-600 dark:text-purple-400 font-medium">TRACKING</p>
                    <p className="text-lg sm:text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                      {Object.keys(minuteROIData).length}
                    </p>
                  </div>
                  <Activity size={24} className="text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Live ROI Timeseries Chart */}
      <Card className="p-4 sm:p-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 flex items-center gap-2">
                <TrendingUp size={20} className="text-primary-600 dark:text-primary-400" />
                Live ROI Timeseries
              </h2>
              <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
                Minute-level ROI and profit per minute for the selected investor
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm text-primary-700 dark:text-primary-300">Hours:</span>
                {['1', '3', '6', '12', '24'].map(val => (
                  <Button
                    key={val}
                    size="sm"
                    variant={liveChartHours === val ? 'primary' : 'ghost'}
                    onClick={() => setLiveChartHours(val)}
                  >
                    {val}h
                  </Button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={selectedLiveInvestor}
                  onChange={(e) => setSelectedLiveInvestor(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 min-w-[200px]"
                >
                  <option value="ALL">All investors</option>
                  <option value="" disabled>Select investor</option>
                  {investors.filter(inv => inv.active !== false).map(inv => (
                    <option key={inv._id} value={inv._id}>
                      {inv.firstName && inv.lastName ? `${inv.firstName} ${inv.lastName}` : inv.email || 'Investor'}
                    </option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => selectedLiveInvestor && fetchMinuteROIChart(selectedLiveInvestor, liveChartHours)}
                  leftIcon={<RefreshCw size={16} className={liveChartLoading ? 'animate-spin' : ''} />}
                  disabled={liveChartLoading || !selectedLiveInvestor}
                >
                  {liveChartLoading ? 'Loading...' : 'Refresh'}
                </Button>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="h-80">
            {liveChartLoading ? (
              <div className="flex items-center justify-center h-full">
                <Spinner size="lg" />
              </div>
            ) : liveChartData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-primary-600 dark:text-primary-400 gap-2">
                <TrendingUp size={28} className="text-primary-400" />
                <p className="font-semibold">No live data yet</p>
                <p className="text-sm">Try refreshing or selecting a different investor/hours window</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={liveChartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" tick={{ fill: 'var(--color-primary-600)' }} />
                  <YAxis yAxisId="left" stroke="#2563eb" domain={['auto', 'auto']} tickFormatter={(v) => `${v}%`} tick={{ fill: 'var(--color-primary-600)' }} />
                  <YAxis yAxisId="right" orientation="right" stroke="#10b981" tickFormatter={(v) => `${v.toFixed(3)}`} tick={{ fill: 'var(--color-primary-600)' }} />
                  <Tooltip formatter={(value, name) => name === 'ROI %' ? `${value.toFixed(2)}%` : `${value.toFixed(4)} / min`} />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="roi" name="ROI %" stroke="#2563eb" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="profit" name="Profit/min" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </Card>

      {/* Daily Profit Timeseries Chart */}
      <Card className="p-4 sm:p-6">
        <div className="space-y-4">
          {/* Chart Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 flex items-center gap-2">
                <TrendingUp size={20} className="text-primary-600 dark:text-primary-400" />
                Total Daily Profit Trend
              </h2>
              <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
                Combined daily profit across {chartFilters.selectedInvestors.length > 0 ? 'selected' : 'all'} investors
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchChartData}
              leftIcon={<RefreshCw size={16} />}
              disabled={chartLoading}
            >
              Refresh
            </Button>
          </div>

          {/* Chart Filters */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="flex items-center gap-2">
              <Filter size={18} className="text-primary-600 dark:text-primary-400" />
              <span className="text-sm font-medium text-primary-700 dark:text-primary-300">Filters:</span>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* Date Range Toggle */}
              <div className="flex items-center gap-2 px-3 py-1.5 border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800">
                <input
                  type="checkbox"
                  id="customDateRange"
                  checked={chartFilters.customDateRange}
                  onChange={(e) => setChartFilters({
                    ...chartFilters,
                    customDateRange: e.target.checked,
                    startDate: e.target.checked ? chartFilters.startDate : null,
                    endDate: e.target.checked ? chartFilters.endDate : null
                  })}
                  className="w-4 h-4 text-primary-600 bg-white dark:bg-primary-700 border-primary-300 dark:border-primary-600 rounded focus:ring-primary-500 focus:ring-2"
                />
                <label htmlFor="customDateRange" className="text-sm text-primary-900 dark:text-primary-50 cursor-pointer">
                  Custom Range
                </label>
              </div>

              {/* Timeframe Filter (shown when custom range is off) */}
              {!chartFilters.customDateRange && (
                <select
                  value={chartFilters.timeframe}
                  onChange={(e) => setChartFilters({ ...chartFilters, timeframe: e.target.value })}
                  className="px-3 py-1.5 text-sm border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="7">Last 7 days</option>
                  <option value="14">Last 14 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="60">Last 60 days</option>
                  <option value="90">Last 90 days</option>
                </select>
              )}

              {/* Date Range Pickers (shown when custom range is on) */}
              {chartFilters.customDateRange && (
                <>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-primary-700 dark:text-primary-300 whitespace-nowrap">From:</label>
                    <input
                      type="date"
                      value={chartFilters.startDate || ''}
                      onChange={(e) => setChartFilters({ ...chartFilters, startDate: e.target.value })}
                      max={chartFilters.endDate || new Date().toISOString().split('T')[0]}
                      className="px-3 py-1.5 text-sm border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-primary-700 dark:text-primary-300 whitespace-nowrap">To:</label>
                    <input
                      type="date"
                      value={chartFilters.endDate || ''}
                      onChange={(e) => setChartFilters({ ...chartFilters, endDate: e.target.value })}
                      min={chartFilters.startDate || undefined}
                      max={new Date().toISOString().split('T')[0]}
                      className="px-3 py-1.5 text-sm border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </>
              )}

              {/* Investor Filter */}
              <div className="flex-1 min-w-[200px]">
                <FormReactSelect
                  isMulti
                  value={chartFilters.selectedInvestors.map(id => {
                    const inv = investors.find(i => i._id === id);
                    return inv ? { value: inv._id, label: `${inv.firstName} ${inv.lastName}` } : null;
                  }).filter(Boolean)}
                  onChange={(selected) => {
                    // Handle both array and non-array cases (cleared selection returns null)
                    const selectedIds = Array.isArray(selected) ? selected.map(s => s.value) : [];
                    setChartFilters({
                      ...chartFilters,
                      selectedInvestors: selectedIds
                    });
                  }}
                  options={investors.map(inv => ({
                    value: inv._id,
                    label: `${inv.firstName} ${inv.lastName}`
                  }))}
                  placeholder="All Investors"
                  className="text-sm"
                />
              </div>

              {/* Clear Filters Button */}
              {(chartFilters.selectedInvestors.length > 0 || chartFilters.customDateRange) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setChartFilters({
                    ...chartFilters,
                    selectedInvestors: [],
                    customDateRange: false,
                    startDate: null,
                    endDate: null
                  })}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Chart */}
          <div className="mt-4">
            {chartLoading ? (
              <div className="flex items-center justify-center h-80">
                <Spinner size="lg" />
              </div>
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="stroke-primary-200 dark:stroke-primary-700" />
                  <XAxis
                    dataKey="displayDate"
                    tick={{ fill: 'currentColor' }}
                    className="text-primary-600 dark:text-primary-400"
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis
                    tick={{ fill: 'currentColor' }}
                    className="text-primary-600 dark:text-primary-400"
                    tickFormatter={(value) => `Đ${value.toLocaleString()}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [`Đ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Daily Profit']}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Area
                    type="monotone"
                    dataKey="totalDailyProfit"
                    stroke="#10b981"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorProfit)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-80 text-primary-600 dark:text-primary-400">
                <BarChart3 size={48} className="mb-3 opacity-50" />
                <p className="text-lg font-medium">No data available</p>
                <p className="text-sm mt-1">Try selecting a different timeframe or investors</p>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full px-4 py-2 border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="sm:w-48">
            <select
              value={filters.sortBy}
              onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
              className="w-full px-4 py-2 border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="name">Sort by Name</option>
              <option value="invested">Sort by Invested</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Investors Table */}
      <Card className="p-6">
        {checkedInvestors.size > 0 && (
          <div className="mb-4 p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-700 rounded-lg flex items-center justify-between">
            <span className="text-warning-900 dark:text-warning-50 font-medium">
              {checkedInvestors.size} selected
            </span>
            <Button
              onClick={handleBulkDelete}
              variant="danger"
              size="sm"
            >
              Delete Selected
            </Button>
          </div>
        )}
        {filteredInvestors.length > 0 ? (
          <DataTable
            columns={[
              {
                key: 'checkbox',
                label: (
                  <input
                    type="checkbox"
                    checked={checkedInvestors.size === filteredInvestors.length && filteredInvestors.length > 0}
                    onChange={toggleAllInvestorsCheck}
                    className="w-4 h-4"
                  />
                ),
                render: (investor) => (
                  <input
                    type="checkbox"
                    checked={checkedInvestors.has(investor._id)}
                    onChange={() => toggleInvestorCheck(investor._id)}
                    className="w-4 h-4"
                  />
                ),
              },
              ...tableColumns
            ]}
            data={filteredInvestors}
            striped
            hover
            exportFileName="investors"
            searchableFields={['firstName', 'lastName', 'email']}
            enableColumnFilters={true}
          />
        ) : (
          <div className="text-center py-12">
            <UsersIcon size={48} className="mx-auto text-primary-300 dark:text-primary-600 mb-4" />
            <p className="text-primary-600 dark:text-primary-400">No investors found</p>
          </div>
        )}
      </Card>

      {/* Investor Details Drawer */}
      <Drawer
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        title={selectedInvestor ? `${selectedInvestor.firstName || ''} ${selectedInvestor.lastName || ''}`.trim() || selectedInvestor.email : 'Investor Details'}
      >
        {investorDetails.loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : selectedInvestor ? (
          <div className="space-y-6">
            {/* Investor Info */}
            <div>
              <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                Investor Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                  <User className="text-primary-400 dark:text-primary-600" size={20} />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Name</p>
                    <p className="text-base font-semibold text-primary-900 dark:text-primary-50">
                      {selectedInvestor.firstName && selectedInvestor.lastName
                        ? `${selectedInvestor.firstName} ${selectedInvestor.lastName}`
                        : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                  <Mail className="text-primary-400 dark:text-primary-600" size={20} />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Email</p>
                    <p className="text-base font-semibold text-primary-900 dark:text-primary-50">
                      {selectedInvestor.email || 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                  <Wallet className="text-primary-400 dark:text-primary-600" size={20} />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Invested Amount</p>
                    <p className="text-base font-semibold text-primary-900 dark:text-primary-50">
                      ${(selectedInvestor.invested_amount || 0).toLocaleString()} USDT
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                  <DollarSign className="text-primary-400 dark:text-primary-600" size={20} />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Remaining Balance</p>
                    <p className="text-base font-semibold text-primary-900 dark:text-primary-50 flex items-center gap-1">
                      <DirhamSymbol size={16} inline />
                      {(selectedInvestor.balance_usdt || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Divider />

            {/* Portfolio Summary */}
            {investorDetails.portfolio && (
              <>
                <div>
                  <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                    Portfolio Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="p-4 bg-primary-50 dark:bg-primary-900/50">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                        Total Value
                      </p>
                      <p className="text-xl font-bold text-primary-900 dark:text-primary-50 mt-1">
                        ${(investorDetails.portfolio.totalValue || 0).toLocaleString()}
                      </p>
                    </Card>
                    <Card className="p-4 bg-primary-50 dark:bg-primary-900/50">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                        Total Invested
                      </p>
                      <p className="text-xl font-bold text-primary-900 dark:text-primary-50 mt-1">
                        ${(investorDetails.portfolio.totalInvested || 0).toLocaleString()}
                      </p>
                    </Card>
                    <Card className={`p-4 col-span-2 ${investorDetails.portfolio.totalGainLoss >= 0 ? 'bg-success-50 dark:bg-success-900/20' : 'bg-error-50 dark:bg-error-900/20'}`}>
                      <p className="text-xs font-medium uppercase tracking-wide">
                        Gain/Loss
                      </p>
                      <p className={`text-xl font-bold mt-1 ${investorDetails.portfolio.totalGainLoss >= 0
                        ? 'text-success-600 dark:text-success-400'
                        : 'text-error-600 dark:text-error-400'
                        }`}>
                        ${(investorDetails.portfolio.totalGainLoss || 0).toLocaleString()}
                      </p>
                    </Card>
                  </div>
                </div>
                <Divider />
              </>
            )}

            {/* Recent Transactions */}
            {investorDetails.transactions.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                  Recent Transactions
                </h3>
                <div className="space-y-2">
                  {investorDetails.transactions.slice(0, 5).map((tx, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-primary-900 dark:text-primary-50 capitalize">
                          {tx.transactionType || 'N/A'}
                        </p>
                        <p className="text-xs text-primary-500 dark:text-primary-400">
                          {new Date(tx.date || tx.createdAt).toISOString().split('T')[0]}
                        </p>
                      </div>
                      <p className={`text-sm font-semibold ${tx.transactionType === 'deposit'
                        ? 'text-success-600 dark:text-success-400'
                        : 'text-error-600 dark:text-error-400'
                        }`}>
                        {tx.transactionType === 'deposit' ? '+' : '-'}
                        <span className="flex items-center gap-1 inline-flex">
                          <DirhamSymbol size={14} inline />
                          {(tx.amount || 0).toLocaleString()}
                        </span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Daily ROI History */}
            {investorDetails.roiHistory && investorDetails.roiHistory.length > 0 && (
              <>
                <Divider />
                <div>
                  <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4 flex items-center gap-2">
                    <BarChart3 size={18} />
                    ROI History (Last 30 Days)
                  </h3>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {investorDetails.roiHistory.slice(0, 10).map((roi, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div>
                          <p className="text-sm font-medium text-primary-900 dark:text-primary-50">
                            {new Date(roi.date).toISOString().split('T')[0]}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 px-2 py-1 rounded-full">
                              {(roi.roi_percentage || 0).toFixed(2)}%
                            </span>
                            <span className="text-xs text-primary-600 dark:text-primary-400">
                              Avg ROI
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <p className="text-sm font-semibold text-success-600 dark:text-success-400 flex items-center gap-1">
                            <Zap size={14} />
                            <DirhamSymbol size={14} inline />
                            {(roi.daily_return || 0).toFixed(2)}
                          </p>
                          <p className="text-xs text-primary-600 dark:text-primary-400">
                            Daily Return
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Allocated Profits History */}
            {investorDetails.allocatedProfits && (
              investorDetails.allocatedProfits.direct_allocations?.length > 0 ||
              investorDetails.allocatedProfits.broker_commissions?.length > 0
            ) && (
                <>
                  <Divider />
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide flex items-center gap-2">
                        <DollarSign size={18} />
                        Allocated Profits
                      </h3>
                      <select
                        value={allocatedProfitsPeriod}
                        onChange={(e) => {
                          setAllocatedProfitsPeriod(e.target.value);
                          if (selectedInvestor) {
                            fetchAllocatedProfits(selectedInvestor._id);
                          }
                        }}
                        className="px-3 py-1 text-sm border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="7">Last 7 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="60">Last 60 days</option>
                        <option value="90">Last 90 days</option>
                      </select>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <Card className="p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-700">
                        <p className="text-xs font-medium text-success-600 dark:text-success-400 uppercase tracking-wide">Direct Allocated</p>
                        <p className="text-lg font-bold text-success-900 dark:text-success-50 mt-1 flex items-center gap-1">
                          <DirhamSymbol size={14} inline />
                          {(investorDetails.allocatedProfits.summary?.total_direct_allocated || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                        </p>
                      </Card>
                      <Card className="p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700">
                        <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide">Commissions (AED)</p>
                        <p className="text-lg font-bold text-primary-900 dark:text-primary-50 mt-1 flex items-center gap-1">
                          <DirhamSymbol size={14} inline />
                          {(investorDetails.allocatedProfits.summary?.total_commissions_aed || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                        </p>
                      </Card>
                    </div>

                    {/* Direct Allocations */}
                    {investorDetails.allocatedProfits.direct_allocations?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-primary-700 dark:text-primary-300 uppercase mb-2">Direct Transaction Allocations</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {investorDetails.allocatedProfits.direct_allocations.map((alloc, idx) => (
                            <div key={idx} className="p-2 bg-success-50 dark:bg-success-900/20 rounded border border-success-200 dark:border-success-800 text-xs">
                              <div className="flex items-center justify-between">
                                <span className="text-success-900 dark:text-success-100 font-semibold">{alloc.transaction_type}</span>
                                <span className="text-success-700 dark:text-success-300 font-bold flex items-center gap-1">
                                  <DirhamSymbol size={12} inline />
                                  {alloc.allocated_amount_aed?.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                                </span>
                              </div>
                              <p className="text-primary-600 dark:text-primary-400 mt-1">
                                {new Date(alloc.allocation_date).toLocaleDateString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Broker Commissions */}
                    {investorDetails.allocatedProfits.broker_commissions?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-primary-700 dark:text-primary-300 uppercase mb-2">Broker Commission Shares</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {investorDetails.allocatedProfits.broker_commissions.map((comm, idx) => (
                            <div key={idx} className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800 text-xs">
                              <div className="flex items-center justify-between">
                                <span className="text-blue-900 dark:text-blue-100 font-semibold">{comm.broker_name}</span>
                                <span className="text-blue-700 dark:text-blue-300 font-bold flex items-center gap-1">
                                  <DirhamSymbol size={12} inline />
                                  {comm.amount_aed?.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                                </span>
                              </div>
                              <p className="text-primary-600 dark:text-primary-400 mt-1">
                                {new Date(comm.date).toLocaleDateString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
          </div>
        ) : null}
      </Drawer>

      {/* Edit Investor Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingInvestor(null);
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            invested_amount: 0,
            balance_usdt: 0,
            roi_min: 0.5,
            roi_max: 1.5,
            startDate: null,
            broker_id: '',
            broker_min_roi: 0.5,
            broker_max_roi: 1.5
          });
        }}
        title="Edit Investor"
      >
        <form onSubmit={handleUpdate} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="First Name"
              name="firstName"
              type="text"
              required
              value={formData.firstName}
              onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              placeholder="Enter first name"
            />
            <FormField
              label="Last Name"
              name="lastName"
              type="text"
              required
              value={formData.lastName}
              onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              placeholder="Enter last name"
            />
          </div>
          <FormField
            label="Email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="Enter email address"
            disabled
          />
          <PasswordField
            label="Password (leave blank to keep current)"
            name="password"
            value={formData.password}
            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
            placeholder="Enter new password (optional)"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="Invested Amount (USDT)"
              name="invested_amount"
              type="number"
              min="0"
              step="0.01"
              value={formData.invested_amount}
              onChange={(e) => setFormData(prev => ({ ...prev, invested_amount: parseFloat(e.target.value) || 0 }))}
              placeholder="0.00"
            />
            <FormField
              label="Balance (AED)"
              name="balance_usdt"
              type="number"
              min="0"
              step="0.01"
              value={formData.balance_usdt}
              onChange={(e) => setFormData(prev => ({ ...prev, balance_usdt: parseFloat(e.target.value) || 0 }))}
              placeholder="0.00"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="ROI Min (%)"
              name="roi_min"
              type="number"
              min="0"
              step="0.01"
              value={formData.roi_min}
              onChange={(e) => setFormData(prev => ({ ...prev, roi_min: parseFloat(e.target.value) || 0 }))}
              placeholder="0.5"
              helperText="New ROI applies to future calculations only"
            />
            <FormField
              label="ROI Max (%)"
              name="roi_max"
              type="number"
              min="0"
              step="0.01"
              value={formData.roi_max}
              onChange={(e) => setFormData(prev => ({ ...prev, roi_max: parseFloat(e.target.value) || 0 }))}
              placeholder="1.5"
              helperText="Historical data remains unchanged"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300">
                Broker
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleOpenBrokerModal}
                className="text-xs"
              >
                <Plus size={14} className="mr-1" />
                Add New Broker
              </Button>
            </div>
            <FormReactSelect
              name="broker_id"
              isClearable
              value={formData.broker_id || ''}
              onChange={(e) => {
                const selectedBrokerId = e?.target?.value || e?.value || '';

                setFormData(prevData => {
                  const updates = {
                    ...prevData,
                    broker_id: selectedBrokerId,
                  };

                  // If a broker is selected, set default ROI values (will be stored in BrokerInvestor model)
                  if (selectedBrokerId) {
                    updates.broker_min_roi = prevData.broker_min_roi || prevData.roi_min || 0.5;
                    updates.broker_max_roi = prevData.broker_max_roi || prevData.roi_max || 1.5;
                  }

                  return updates;
                });
              }}
              options={brokers.map(b => ({ value: b._id, label: b.name }))}
              placeholder="Select broker (optional)"
              className="text-sm"
            />
            <p className="mt-1 text-xs text-primary-600 dark:text-primary-400">
              Choose the broker linked to this investor
            </p>
          </div>
          {formData.broker_id && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <FormField
                label="Broker ROI Min (%)"
                name="broker_min_roi"
                type="number"
                min="0"
                step="0.01"
                value={formData.broker_min_roi}
                onChange={(e) => setFormData(prev => ({ ...prev, broker_min_roi: parseFloat(e.target.value) || 0 }))}
                placeholder="0.0"
                helperText="Broker's minimum ROI for this investor (stored in BrokerInvestor model)"
              />
              <FormField
                label="Broker ROI Max (%)"
                name="broker_max_roi"
                type="number"
                min="0"
                step="0.01"
                value={formData.broker_max_roi}
                onChange={(e) => setFormData(prev => ({ ...prev, broker_max_roi: parseFloat(e.target.value) || 0 }))}
                placeholder="0.0"
                helperText="Broker's maximum ROI for this investor (stored in BrokerInvestor model)"
              />
            </div>
          )}
          <FormDatePicker
            label="Start Date"
            name="startDate"
            value={formData.startDate}
            onChange={(e) => {
              // FormDatePicker now sends YYYY-MM-DD string, parse it as local date
              if (e.target.value) {
                const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (dateMatch) {
                  const year = parseInt(dateMatch[1], 10);
                  const month = parseInt(dateMatch[2], 10) - 1; // Month is 0-indexed
                  const day = parseInt(dateMatch[3], 10);
                  const dateValue = new Date(year, month, day);
                  setFormData(prev => ({ ...prev, startDate: dateValue }));
                } else {
                  setFormData(prev => ({ ...prev, startDate: null }));
                }
              } else {
                setFormData(prev => ({ ...prev, startDate: null }));
              }
            }}
            placeholderText="Select start date"
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setShowEditModal(false);
                setEditingInvestor(null);
                setFormData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  password: '',
                  invested_amount: 0,
                  balance_usdt: 0,
                  roi_min: 0.5,
                  roi_max: 1.5,
                  startDate: null,
                  broker_id: '',
                  broker_min_roi: 0.5,
                  broker_max_roi: 1.5
                });
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
            >
              Update Investor
            </Button>
          </div>
        </form>
      </Modal>

      {/* Add Investor Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          // Reset form when modal closes
          setFormData({
            fullName: '',
            email: '',
            password: '',
            invested_amount: 0,
            roi_min: 0.5,
            roi_max: 1.5,
            startDate: null,
            broker_id: '',
            broker_min_roi: 0.5,
            broker_max_roi: 1.5
          });
        }}
        title="Add New Investor"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField
            label="Full Name"
            name="fullName"
            type="text"
            required
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            placeholder="Enter full name"
          />
          <FormField
            label="Email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="Enter email address"
          />
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300">
                Password <span className="text-error-600">*</span>
              </label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleGeneratePassword}
                  leftIcon={<RefreshCw size={14} />}
                >
                  Auto Generate
                </Button>
                {formData.password && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyPassword}
                    leftIcon={<Copy size={14} />}
                  >
                    Copy
                  </Button>
                )}
              </div>
            </div>
            <PasswordField
              name="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Enter password"
            />
          </div>
          <FormField
            label="Investment Amount (USD)"
            name="invested_amount"
            type="number"
            min="0"
            step="0.01"
            value={formData.invested_amount}
            onChange={(e) => setFormData({ ...formData, invested_amount: parseFloat(e.target.value) || 0 })}
            placeholder="0.00"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="ROI Min (%)"
              name="roi_min"
              type="number"
              min="0"
              step="0.01"
              value={formData.roi_min}
              onChange={(e) => setFormData({ ...formData, roi_min: parseFloat(e.target.value) || 0 })}
              placeholder="0.5"
            />
            <FormField
              label="ROI Max (%)"
              name="roi_max"
              type="number"
              min="0"
              step="0.01"
              value={formData.roi_max}
              onChange={(e) => setFormData({ ...formData, roi_max: parseFloat(e.target.value) || 0 })}
              placeholder="1.5"
            />
          </div>
          <FormDatePicker
            label="Start Date"
            name="startDate"
            value={formData.startDate}
            onChange={(e) => {
              // FormDatePicker now sends YYYY-MM-DD string, parse it as local date
              if (e.target.value) {
                const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (dateMatch) {
                  const year = parseInt(dateMatch[1], 10);
                  const month = parseInt(dateMatch[2], 10) - 1; // Month is 0-indexed
                  const day = parseInt(dateMatch[3], 10);
                  const dateValue = new Date(year, month, day);
                  setFormData({ ...formData, startDate: dateValue });
                } else {
                  setFormData({ ...formData, startDate: null });
                }
              } else {
                setFormData({ ...formData, startDate: null });
              }
            }}
            placeholderText="Select start date"
          />
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300">
                Broker
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleOpenBrokerModal}
                leftIcon={<Plus size={14} />}
              >
                Add New Broker
              </Button>
            </div>
            <FormReactSelect
              name="broker_id"
              options={brokers.map(broker => ({
                value: broker._id,
                label: `${broker.name} (${broker.email})`
              }))}
              value={formData.broker_id || ''}
              onChange={(e) => {
                const brokerId = e.target.value || '';
                setFormData({
                  ...formData,
                  broker_id: brokerId,
                  // Set default broker ROI values (will be stored in BrokerInvestor model)
                  broker_min_roi: brokerId ? 0 : 0,
                  broker_max_roi: brokerId ? 0 : 0
                });
              }}
              placeholder="Select a broker..."
            />
          </div>
          {formData.broker_id && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="Broker Min ROI (%)"
                name="broker_min_roi"
                type="number"
                min="0"
                step="0.01"
                value={formData.broker_min_roi}
                onChange={(e) => setFormData({ ...formData, broker_min_roi: parseFloat(e.target.value) || 0 })}
                placeholder="0.5"
              />
              <FormField
                label="Broker Max ROI (%)"
                name="broker_max_roi"
                type="number"
                min="0"
                step="0.01"
                value={formData.broker_max_roi}
                onChange={(e) => setFormData({ ...formData, broker_max_roi: parseFloat(e.target.value) || 0 })}
                placeholder="1.5"
              />
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={createInvestorLoading}
              leftIcon={createInvestorLoading ? <Spinner size="sm" /> : null}
            >
              {createInvestorLoading ? 'Adding...' : 'Add Investor'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Add Broker Modal */}
      <Modal
        isOpen={showBrokerModal}
        onClose={() => setShowBrokerModal(false)}
        title="Create Broker"
      >
        <form onSubmit={handleCreateBroker} className="space-y-4">
          <FormField
            label="Broker Name"
            name="name"
            type="text"
            required
            value={brokerFormData.name}
            onChange={(e) => setBrokerFormData({ ...brokerFormData, name: e.target.value })}
            placeholder="Enter broker name"
          />
          <FormField
            label="Email"
            name="email"
            type="email"
            required
            value={brokerFormData.email}
            onChange={(e) => setBrokerFormData({ ...brokerFormData, email: e.target.value })}
            placeholder="Enter email address"
          />
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300">
                Password <span className="text-error-600">*</span>
              </label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateBrokerPassword}
                  leftIcon={<RefreshCw size={14} />}
                >
                  Auto Generate
                </Button>
                {brokerFormData.password && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyBrokerPassword}
                    leftIcon={<Copy size={14} />}
                  >
                    Copy
                  </Button>
                )}
              </div>
            </div>
            <PasswordField
              name="password"
              required
              value={brokerFormData.password}
              onChange={(e) => setBrokerFormData({ ...brokerFormData, password: e.target.value })}
              placeholder="Enter password for broker login"
            />
          </div>
          <FormDatePicker
            label="Start Date"
            name="brokerStartDate"
            value={brokerFormData.startDate}
            onChange={(e) => {
              if (e.target.value) {
                const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (dateMatch) {
                  const year = parseInt(dateMatch[1], 10);
                  const month = parseInt(dateMatch[2], 10) - 1;
                  const day = parseInt(dateMatch[3], 10);
                  setBrokerFormData({ ...brokerFormData, startDate: new Date(year, month, day) });
                }
              } else {
                setBrokerFormData({ ...brokerFormData, startDate: null });
              }
            }}
            placeholderText="Select start date"
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowBrokerModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
            >
              Create Broker
            </Button>
          </div>
        </form>
      </Modal>

      {/* History Modal */}
      <Modal
        isOpen={showHistoryModal}
        onClose={() => {
          setShowHistoryModal(false);
          setHistoryData([]);
          setHistoryInvestor(null);
          setRegeneratingProfits(false);
          cancelEditingHistoryRecord();
        }}
        title={`Earning History - ${historyInvestor?.firstName} ${historyInvestor?.lastName}`}
        size="xl"
      >
        <div className="space-y-4">
          {/* Date Filters */}
          <div className="flex flex-wrap gap-3 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                Start Date
              </label>
              <FormDatePicker
                name="historyStartDate"
                value={historyFilters.startDate}
                onChange={(e) => {
                  if (e.target.value) {
                    const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (dateMatch) {
                      const year = parseInt(dateMatch[1], 10);
                      const month = parseInt(dateMatch[2], 10) - 1;
                      const day = parseInt(dateMatch[3], 10);
                      setHistoryFilters({ ...historyFilters, startDate: new Date(year, month, day) });
                    }
                  } else {
                    setHistoryFilters({ ...historyFilters, startDate: null });
                  }
                }}
                placeholder="Select start date"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                End Date
              </label>
              <FormDatePicker
                name="historyEndDate"
                value={historyFilters.endDate}
                onChange={(e) => {
                  if (e.target.value) {
                    const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (dateMatch) {
                      const year = parseInt(dateMatch[1], 10);
                      const month = parseInt(dateMatch[2], 10) - 1;
                      const day = parseInt(dateMatch[3], 10);
                      setHistoryFilters({ ...historyFilters, endDate: new Date(year, month, day) });
                    }
                  } else {
                    setHistoryFilters({ ...historyFilters, endDate: null });
                  }
                }}
                placeholder="Select end date"
              />
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <Button
                onClick={applyHistoryFilters}
                variant="primary"
                size="md"
                disabled={regeneratingProfits}
              >
                <Filter size={16} className="mr-2" />
                Apply Filter
              </Button>
              <Button
                type="button"
                onClick={handleRegenerateProfits}
                variant="outline"
                size="md"
                disabled={regeneratingProfits || historyLoading || !historyInvestor}
                title="Recalculate daily ROI from minute-level data using current rules"
              >
                <RefreshCw size={16} className={`mr-2 ${regeneratingProfits ? 'animate-spin' : ''}`} />
                Regenerate Profits
              </Button>
            </div>
          </div>

          {/* How regeneration is calculated (real values for this investor) */}
          {historyInvestor && (() => {
            const rate = Number(historyInvestor.aed_conversion_rate) || parseFloat(import.meta.env.VITE_USD_TO_AED_RATE) || 3.662;
            const investedUsdt = Number(historyInvestor.invested_amount || 0);
            const roiMin = Number(historyInvestor.roi_min ?? 0);
            const roiMax = Number(historyInvestor.roi_max ?? 0);
            const avgRoi = (roiMin + roiMax) / 2;
            const minutesInMonth = 30 * 24 * 60;
            const monthlyReturnAedSample = investedUsdt * (avgRoi / 100) * rate;
            const profitPerMinSample = minutesInMonth > 0 ? monthlyReturnAedSample / minutesInMonth : 0;
            const dailyProfitSample = profitPerMinSample * 24 * 60;
            const startForRecalc = historyInvestor.startDate || historyInvestor.createdAt;
            return (
              <div className="rounded-lg border border-primary-200 dark:border-primary-700 bg-primary-50/80 dark:bg-primary-900/30 p-4 text-sm text-primary-800 dark:text-primary-200 space-y-2">
                <p className="font-semibold text-primary-900 dark:text-primary-50">How &quot;Regenerate Profits&quot; works (with your current inputs)</p>
                <ul className="list-disc list-inside space-y-1.5 text-primary-700 dark:text-primary-300">
                  <li>
                    Backend recalculates from{' '}
                    <strong>
                      {startForRecalc
                        ? new Date(startForRecalc).toLocaleDateString()
                        : 'investment start'}
                    </strong>
                    : minute-level profit is set as{' '}
                    <code className="text-xs bg-primary-100 dark:bg-primary-800 px-1 rounded">
                      monthly_return_aed = invested_usdt × (ROI% ÷ 100) × {rate.toFixed(4)} (AED rate)
                    </code>
                    , then{' '}
                    <code className="text-xs bg-primary-100 dark:bg-primary-800 px-1 rounded">
                      profit_per_minute = monthly_return_aed ÷ {minutesInMonth.toLocaleString()}
                    </code>{' '}
                    (30-day month).
                  </li>
                  <li>
                    Using this investor&apos;s numbers:{' '}
                    <strong>{investedUsdt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT</strong>, ROI{' '}
                    <strong>
                      {roiMin.toFixed(2)}% – {roiMax.toFixed(2)}%
                    </strong>{' '}
                    (avg <strong>{avgRoi.toFixed(4)}%</strong>), rate <strong>{rate.toFixed(4)}</strong> → sample monthly AED ≈{' '}
                    <strong>{monthlyReturnAedSample.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>, sample ≈{' '}
                    <strong>{profitPerMinSample.toFixed(6)}</strong> AED/min → one day ≈{' '}
                    <strong>{dailyProfitSample.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> AED (if ROI were flat at avg; actual rows vary by stored ROI%).
                  </li>
                  <li>
                    Daily table values are then rebuilt by aggregating minutes per calendar day; broker commission is derived from the broker link (avg broker ROI × investor profit per minute).
                  </li>
                  <li>
                    <strong>Units:</strong> Daily profit, monthly estimate, and broker commission in the table are all <strong>AED</strong>. The backend stores <strong>minute-level</strong> profit; daily values are <strong>extrapolated to a full 24h day</strong> (average AED/min × 1,440) so they align with the formulas above even when fewer than 1,440 minute rows exist for that calendar day (cron gaps, downtime). The sample &quot;one day&quot; uses flat average ROI; actual rows still vary with the blended ROI% for that day.
                  </li>
                </ul>
              </div>
            );
          })()}

          {/* History Table */}
          {regeneratingProfits ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-primary-700 dark:text-primary-300 px-1">
                <RefreshCw className="h-4 w-4 animate-spin text-primary-600 shrink-0" />
                <span>Regenerating historical profits… This may take a minute.</span>
              </div>
              <div className="overflow-x-auto rounded-lg border border-primary-200 dark:border-primary-700">
                <table className="min-w-full divide-y divide-primary-200 dark:divide-primary-700">
                  <thead className="bg-primary-50 dark:bg-primary-900/50">
                    <tr>
                      {['Date', 'ROI %', 'Daily Profit', 'Monthly Estimate', 'Broker Commission', 'Actions'].map((h, idx) => (
                        <th
                          key={h}
                          className={`px-4 py-3 text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider ${idx === 0 ? 'text-left' : 'text-right'}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-primary-800 divide-y divide-primary-200 dark:divide-primary-700">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-primary-50/30 dark:bg-primary-900/10' : ''}>
                        <td className="px-4 py-3">
                          <div className="h-4 rounded bg-primary-200/80 dark:bg-primary-600/40 animate-pulse w-28" />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="h-4 rounded bg-primary-200/80 dark:bg-primary-600/40 animate-pulse w-16 ml-auto" />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="h-4 rounded bg-primary-200/80 dark:bg-primary-600/40 animate-pulse w-24 ml-auto" />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="h-4 rounded bg-primary-200/80 dark:bg-primary-600/40 animate-pulse w-24 ml-auto" />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="h-4 rounded bg-primary-200/80 dark:bg-primary-600/40 animate-pulse w-20 ml-auto" />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="h-8 rounded bg-primary-200/80 dark:bg-primary-600/40 animate-pulse w-16 ml-auto" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : historyLoading ? (
            <div className="flex justify-center items-center py-12">
              <Spinner size="lg" />
            </div>
          ) : historyData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-primary-200 dark:divide-primary-700">
                <thead className="bg-primary-50 dark:bg-primary-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      ROI %
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Daily Profit
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Monthly Estimate
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Broker Commission
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-primary-800 divide-y divide-primary-200 dark:divide-primary-700">
                  {historyData.map((record, index) => {
                    // DailyROI.daily_return / monthly_return / broker_* are AED (backend aggregates minute AED profit)
                    const dailyProfitAED = record.daily_return || 0;
                    const monthlyEstimateAED = record.monthly_return != null ? record.monthly_return : dailyProfitAED * 30;
                    const brokerCommissionAED = record.broker_daily_return || 0;
                    const isEditing = editingHistoryId === record._id;
                    return (
                      <tr key={record._id || index} className={index % 2 === 0 ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}>
                        <td className="px-4 py-3 text-sm text-primary-900 dark:text-primary-50">
                          {new Date(record.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={editingHistoryValues.roi_percentage}
                              onChange={(e) => setEditingHistoryValues({ ...editingHistoryValues, roi_percentage: e.target.value })}
                              className="w-24 ml-auto text-right px-2 py-1 rounded border border-primary-300 dark:border-primary-600 bg-white dark:bg-primary-900"
                            />
                          ) : (
                            `${(record.roi_percentage || 0).toFixed(2)}%`
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={editingHistoryValues.daily_profit_aed}
                              onChange={(e) => setEditingHistoryValues({ ...editingHistoryValues, daily_profit_aed: e.target.value })}
                              className="w-28 ml-auto text-right px-2 py-1 rounded border border-primary-300 dark:border-primary-600 bg-white dark:bg-primary-900"
                            />
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <DirhamSymbol size={14} inline />
                              {dailyProfitAED.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                          <div className="flex items-center justify-end gap-1">
                            <DirhamSymbol size={14} inline />
                            {(isEditing ? Number(editingHistoryValues.daily_profit_aed || 0) * 30 : monthlyEstimateAED).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={editingHistoryValues.broker_commission_aed}
                              onChange={(e) => setEditingHistoryValues({ ...editingHistoryValues, broker_commission_aed: e.target.value })}
                              className="w-28 ml-auto text-right px-2 py-1 rounded border border-primary-300 dark:border-primary-600 bg-white dark:bg-primary-900"
                            />
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <DirhamSymbol size={14} inline />
                              {brokerCommissionAED.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {isEditing ? (
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={() => saveHistoryRecord(record)}
                                disabled={savingHistory}
                              >
                                {savingHistory ? 'Saving...' : 'Save'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelEditingHistoryRecord}
                                disabled={savingHistory}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEditingHistoryRecord(record)}
                            >
                              Edit
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center text-primary-600 dark:text-primary-400 py-12">
              No history data available for the selected period
            </div>
          )}
        </div>
      </Modal>

      {/* Add Investment Modal */}
      <Modal
        isOpen={showInvestmentModal}
        onClose={() => {
          setShowInvestmentModal(false);
          setSelectedInvestorForInvestment(null);
          setNewInvestmentForm({ amount: '', date: new Date(), note: '' });
        }}
        title="Add Additional Investment"
      >
        <div className="space-y-4">
          {selectedInvestorForInvestment && (
            <div className="bg-primary-50 dark:bg-primary-900/50 p-4 rounded-lg">
              <p className="text-sm text-primary-700 dark:text-primary-300">
                <span className="font-medium">Investor:</span>{' '}
                {selectedInvestorForInvestment.firstName} {selectedInvestorForInvestment.lastName}
              </p>
              <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                <span className="font-medium">Current Investment:</span>{' '}
                ${(selectedInvestorForInvestment.invested_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
              </p>
            </div>
          )}

          <FormField
            label="Investment Amount (USDT)"
            name="amount"
            type="number"
            required
            value={newInvestmentForm.amount}
            onChange={(e) => setNewInvestmentForm({ ...newInvestmentForm, amount: e.target.value })}
            placeholder="Enter investment amount"
            step="0.01"
            min="0.01"
          />

          <FormDatePicker
            label="Investment Date"
            name="date"
            required
            value={newInvestmentForm.date}
            onChange={(e) => {
              // FormDatePicker sends event with date string in e.target.value
              const dateValue = e.target.value;
              if (dateValue) {
                // Parse the YYYY-MM-DD string as a local date
                const dateMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (dateMatch) {
                  const year = parseInt(dateMatch[1], 10);
                  const month = parseInt(dateMatch[2], 10) - 1;
                  const day = parseInt(dateMatch[3], 10);
                  const date = new Date(year, month, day);
                  setNewInvestmentForm({ ...newInvestmentForm, date });
                } else {
                  setNewInvestmentForm({ ...newInvestmentForm, date: new Date(dateValue) });
                }
              } else {
                setNewInvestmentForm({ ...newInvestmentForm, date: new Date() });
              }
            }}
          />

          <FormField
            label="Note (Optional)"
            name="note"
            type="text"
            value={newInvestmentForm.note}
            onChange={(e) => setNewInvestmentForm({ ...newInvestmentForm, note: e.target.value })}
            placeholder="Add a note about this investment"
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowInvestmentModal(false);
                setSelectedInvestorForInvestment(null);
                setNewInvestmentForm({ amount: '', date: new Date(), note: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={investmentLoading || !newInvestmentForm.amount}
              leftIcon={investmentLoading ? <Spinner size="sm" /> : <Plus size={16} />}
              onClick={handleAddInvestment}
            >
              {investmentLoading ? 'Adding...' : 'Add Investment'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Adjust Investment Modal */}
      <Modal
        isOpen={showAdjustInvestmentModal}
        onClose={() => {
          setShowAdjustInvestmentModal(false);
          setAdjustInvestmentForm({ mode: 'reduce', amount: '', date: new Date(), note: '' });
        }}
        title="Adjust Investment"
      >
        <div className="space-y-4">
          {selectedInvestorForInvestment && (
            <div className="bg-primary-50 dark:bg-primary-900/50 p-4 rounded-lg">
              <p className="text-sm text-primary-700 dark:text-primary-300">
                <span className="font-medium">Investor:</span>{' '}
                {selectedInvestorForInvestment.firstName} {selectedInvestorForInvestment.lastName}
              </p>
              <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                <span className="font-medium">Current Investment:</span>{' '}
                ${(selectedInvestorForInvestment.invested_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
              Adjustment Type
            </label>
            <select
              value={adjustInvestmentForm.mode}
              onChange={(e) => setAdjustInvestmentForm({ ...adjustInvestmentForm, mode: e.target.value })}
              className="w-full px-3 py-2 border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50"
            >
              <option value="reduce">Reduce Investment</option>
              <option value="set">Edit Total Investment</option>
            </select>
          </div>

          <FormField
            label={adjustInvestmentForm.mode === 'reduce' ? 'Reduce Amount (USDT)' : 'New Total Investment (USDT)'}
            name="adjust_amount"
            type="number"
            required
            value={adjustInvestmentForm.amount}
            onChange={(e) => setAdjustInvestmentForm({ ...adjustInvestmentForm, amount: e.target.value })}
            placeholder={adjustInvestmentForm.mode === 'reduce' ? 'Enter reduction amount' : 'Enter new total investment'}
            step="0.01"
            min="0"
          />

          <FormDatePicker
            label="Effective Date"
            name="adjustDate"
            required
            value={adjustInvestmentForm.date}
            onChange={(e) => {
              const dateValue = e.target.value;
              if (dateValue) {
                const dateMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (dateMatch) {
                  const year = parseInt(dateMatch[1], 10);
                  const month = parseInt(dateMatch[2], 10) - 1;
                  const day = parseInt(dateMatch[3], 10);
                  const parsedDate = new Date(year, month, day);
                  setAdjustInvestmentForm({ ...adjustInvestmentForm, date: parsedDate });
                } else {
                  setAdjustInvestmentForm({ ...adjustInvestmentForm, date: new Date(dateValue) });
                }
              } else {
                setAdjustInvestmentForm({ ...adjustInvestmentForm, date: new Date() });
              }
            }}
          />

          <FormField
            label="Note (Optional)"
            name="adjust_note"
            type="text"
            value={adjustInvestmentForm.note}
            onChange={(e) => setAdjustInvestmentForm({ ...adjustInvestmentForm, note: e.target.value })}
            placeholder="Reason for adjustment"
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAdjustInvestmentModal(false);
                setAdjustInvestmentForm({ mode: 'reduce', amount: '', date: new Date(), note: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={adjustInvestmentLoading || adjustInvestmentForm.amount === ''}
              leftIcon={adjustInvestmentLoading ? <Spinner size="sm" /> : <Edit size={16} />}
              onClick={handleAdjustInvestment}
            >
              {adjustInvestmentLoading ? 'Applying...' : 'Apply Adjustment'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Investment History Modal */}
      <Modal
        isOpen={showInvestmentHistoryModal}
        onClose={() => {
          setShowInvestmentHistoryModal(false);
          setSelectedInvestorForInvestment(null);
          setInvestmentData([]);
        }}
        title="Investment History"
        size="lg"
      >
        <div className="space-y-4">
          {selectedInvestorForInvestment && (
            <div className="bg-primary-50 dark:bg-primary-900/50 p-4 rounded-lg">
              <p className="text-sm text-primary-700 dark:text-primary-300">
                <span className="font-medium">Investor:</span>{' '}
                {selectedInvestorForInvestment.firstName} {selectedInvestorForInvestment.lastName}
              </p>
              <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                <span className="font-medium">Total Investment:</span>{' '}
                ${(selectedInvestorForInvestment.invested_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
              </p>
            </div>
          )}

          {investmentLoading ? (
            <div className="flex justify-center items-center py-12">
              <Spinner size="lg" />
            </div>
          ) : investmentData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-primary-200 dark:divide-primary-700">
                <thead className="bg-primary-50 dark:bg-primary-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Amount (USDT)
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Amount (AED)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Note
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Added By
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wider">
                      Snapshot
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-primary-800 divide-y divide-primary-200 dark:divide-primary-700">
                  {investmentData.map((record, index) => {
                    const amountAED = (record.amount || 0) * (parseFloat(import.meta.env.VITE_USD_TO_AED_RATE) || 3.662);
                    const typeLabelMap = {
                      initial: 'Initial',
                      additional: 'Additional',
                      reduction: 'Reduction',
                      adjustment: 'Adjustment'
                    };
                    const typeVariantMap = {
                      initial: 'primary',
                      additional: 'success',
                      reduction: 'error',
                      adjustment: 'info'
                    };
                    return (
                      <tr key={record._id || index} className={index % 2 === 0 ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}>
                        <td className="px-4 py-3 text-sm text-primary-900 dark:text-primary-50">
                          {new Date(record.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </td>
                        <td className="px-4 py-3 text-sm text-primary-900 dark:text-primary-50">
                          <Badge
                            variant={typeVariantMap[record.type] || 'primary'}
                            size="sm"
                          >
                            {typeLabelMap[record.type] || record.type || 'Unknown'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50 font-medium">
                          ${(record.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-primary-900 dark:text-primary-50">
                          <div className="flex items-center justify-end gap-1">
                            <DirhamSymbol size={14} inline />
                            {amountAED.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-primary-900 dark:text-primary-50">
                          {record.note || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-primary-900 dark:text-primary-50">
                          {record.admin_name || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-primary-900 dark:text-primary-50">
                          {(record.previous_invested_amount !== null && record.previous_invested_amount !== undefined) ||
                            (record.balance_snapshot_aed !== null && record.balance_snapshot_aed !== undefined) ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewInvestmentSnapshot(record)}
                            >
                              View
                            </Button>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center text-primary-600 dark:text-primary-400 py-12">
              No investment history available
            </div>
          )}
        </div>
      </Modal>

      {/* Investment Snapshot Modal */}
      <Modal
        isOpen={showInvestmentSnapshotModal}
        onClose={() => {
          setShowInvestmentSnapshotModal(false);
          setSelectedInvestmentSnapshot(null);
        }}
        title="Investment Snapshot"
      >
        {selectedInvestmentSnapshot ? (
          <div className="space-y-3">
            <div className="p-3 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
              <p className="text-sm text-primary-700 dark:text-primary-300">
                Snapshot Date: {new Date(selectedInvestmentSnapshot.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Card className="p-3">
                <p className="text-xs text-primary-600 dark:text-primary-400 uppercase">Previous Invested (USDT)</p>
                <p className="text-base font-semibold text-primary-900 dark:text-primary-50 mt-1">
                  {Number(selectedInvestmentSnapshot.previous_invested_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </Card>
              <Card className="p-3">
                <p className="text-xs text-primary-600 dark:text-primary-400 uppercase">New Invested (USDT)</p>
                <p className="text-base font-semibold text-primary-900 dark:text-primary-50 mt-1">
                  {Number(selectedInvestmentSnapshot.new_invested_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </Card>
              <Card className="p-3">
                <p className="text-xs text-primary-600 dark:text-primary-400 uppercase">Profit Until Date (AED)</p>
                <p className="text-base font-semibold text-success-600 dark:text-success-400 mt-1">
                  {Number(selectedInvestmentSnapshot.profit_snapshot_aed || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </Card>
              <Card className="p-3">
                <p className="text-xs text-primary-600 dark:text-primary-400 uppercase">Net Balance Until Date (AED)</p>
                <p className="text-base font-semibold text-primary-900 dark:text-primary-50 mt-1">
                  {Number(selectedInvestmentSnapshot.balance_snapshot_aed || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </Card>
            </div>
          </div>
        ) : null}
      </Modal>

      <BulkInvestorImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onSuccess={() => {
          setShowBulkImportModal(false);
          fetchInvestors();
          fetchBrokerInvestors();
          fetchBrokers();
        }}
      />
    </div>
  );
};

export default Investors;
